import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeftOutlined, SaveOutlined, UpOutlined, SettingOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Space, message, Typography, Input, Tabs } from 'antd';
import { EFormDesignerUpload, EFormDesignerAIChat, EFormFieldDisplaySetting } from '../components/EFormDesigner';
import AIGeneratingOverlay from '../components/WorkflowDesigner/components/AIGeneratingOverlay';
import useGrapesJS from '../hooks/useGrapesJS';
import { useLanguage } from '../contexts/LanguageContext';
import './EFormDesigner.css';
import {
  TextInputEditor,
  TextAreaEditor,
  SelectEditor,
  RadioEditor,
  CheckboxEditor,
  ButtonEditor,
  LabelEditor,
  TextBlockEditor,
  ImageEditor,
  TableCellEditor
} from '../components/EFormDesigner/editors';

// 添加紫色返回按鈕的 hover 樣式
const purpleButtonStyle = `
  .purple-back-button:hover {
    background-color: #8c4dd4 !important;
    border-color: #8c4dd4 !important;
  }
`;

const { Title } = Typography;

const EFormDesigner = ({ initialSchema, onSave, onBack }) => {
  const { t } = useLanguage();
  
  // 基本狀態
  const [htmlContent, setHtmlContent] = useState(initialSchema?.htmlCode || initialSchema?.html || '');
  const [formName, setFormName] = useState(initialSchema?.name || t('eformDesigner.newForm'));
  const [formDescription, setFormDescription] = useState(initialSchema?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isToolbarCollapsed, setIsToolbarCollapsed] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  
  // 上傳相關狀態
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  // AI 相關狀態（嵌入式對話框使用）
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  
  // 字段顯示設定相關狀態
  const [fieldDisplaySettingVisible, setFieldDisplaySettingVisible] = useState(false);
  const [fieldDisplaySettings, setFieldDisplaySettings] = useState([]);
  
  // 編輯相關狀態
  const urlParams = new URLSearchParams(window.location.search);
  const editFormId = urlParams.get('edit');
  const [isEditing, setIsEditing] = useState(!!initialSchema?.id || !!editFormId);
  const [formId, setFormId] = useState(initialSchema?.id || editFormId);
  
  // 編輯相關狀態 - 改為右側面板
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  
  // GrapesJS 相關
  const editorRef = useRef(null);
  const importFileInputRef = useRef(null);
  console.log(' EFormDesigner: 開始調用 useGrapesJS...');

  // 載入表單內容的函數
  const loadFormContent = async (formId) => {
    setIsLoadingForm(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('請先登入');
        return;
      }

      const response = await fetch(`/api/eforms/${formId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const formData = await response.json();
      console.log('📥 載入的表單數據:', formData);

      // 更新表單內容
      if (formData.htmlCode) {
        // 檢查載入的 HTML 中是否包含正確的 img src
        console.log('📥 載入表單 HTML，檢查 img 標籤...');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formData.htmlCode.replace(/<style[^>]*>[\s\S]*?<\/style>/g, '');
        const imgTags = tempDiv.querySelectorAll('img');
        console.log(`🔍 載入的 HTML 中包含 ${imgTags.length} 個 img 標籤`);
        imgTags.forEach((img, index) => {
          const src = img.getAttribute('src');
          console.log(`  img ${index + 1} src:`, src ? src.substring(0, 100) : '無 src');
        });
        
        setHtmlContent(formData.htmlCode);
        if (grapesEditor) {
          grapesEditor.setComponents(formData.htmlCode);
        }
      }
      
      if (formData.name) {
        setFormName(formData.name);
      }
      
      if (formData.description) {
        setFormDescription(formData.description);
      }

      // 載入字段顯示設定
      console.log('🔍 載入的 fieldDisplaySettings 原始數據:', formData.fieldDisplaySettings);
      if (formData.fieldDisplaySettings) {
        try {
          const settings = JSON.parse(formData.fieldDisplaySettings);
          console.log('🔍 解析後的字段設定:', settings);
          setFieldDisplaySettings(settings);
        } catch (error) {
          console.warn('解析字段顯示設定失敗:', error);
          setFieldDisplaySettings([]);
        }
      } else {
        console.log('🔍 沒有字段顯示設定數據');
        setFieldDisplaySettings([]);
      }

      message.success('表單內容載入成功！');
    } catch (error) {
      console.error('❌ 載入表單內容失敗:', error);
      message.error('載入表單內容失敗: ' + error.message);
    } finally {
      setIsLoadingForm(false);
    }
  };

  const { editor: grapesEditor, isReady: isEditorReady } = useGrapesJS(
    editorRef, 
    htmlContent,
    (readyEditor) => {
      // 當編輯器準備好時，設置事件監聽器
      console.log('🎧 編輯器準備好，開始設置事件監聽器...');
      console.log('🎧 編輯器實例:', readyEditor);
      console.log('🎧 編輯器方法:', Object.getOwnPropertyNames(Object.getPrototypeOf(readyEditor)));
      
      // 檢查編輯器是否有 on 方法
      if (typeof readyEditor.on === 'function') {
        console.log('✅ 編輯器有 on 方法，可以設置事件監聽器');
        
        // 監聽編輯組件請求
        readyEditor.on('edit-component-requested', (component) => {
          // 添加安全檢查
          if (!component || !component.get || typeof component.get !== 'function') {
            console.warn('⚠️ edit-component-requested: 組件無效或已被銷毀');
            return;
          }
          
          try {
            const tagName = component.get('tagName');
            const componentId = component.getId();
            
            console.log('📱 收到編輯組件請求:', component);
            console.log('📱 組件標籤:', tagName);
            console.log('📱 組件ID:', componentId);
            
            // 使用函數式更新確保狀態正確更新
            setSelectedComponent(component);
            setEditPanelOpen(true);
            
            console.log('✅ 右側面板狀態已設置為開啟');
            console.log('🔍 當前 editPanelOpen 狀態:', true);
            console.log('🔍 當前 selectedComponent:', component);
          } catch (error) {
            console.error('❌ edit-component-requested 事件處理錯誤:', error);
            console.error('❌ 組件狀態:', {
              hasGet: !!component.get,
              getType: typeof component.get,
              component: component
            });
          }
        });
        
        // 監聽內容變化
        readyEditor.on('component:update', () => {
          console.log('🔄 組件更新事件觸發，重新獲取 HTML 內容');
          const html = readyEditor.getHtml();
          const css = readyEditor.getCss();
          const newContent = `<style>${css}</style>${html}`;
          console.log('🔄 新的 HTML 內容長度:', newContent.length);
          setHtmlContent(newContent);
        });
        
        // 監聽編輯器內容更新事件
        readyEditor.on('component:update', () => {
          console.log('🔄 編輯器內容更新事件觸發');
          // 這個事件會在組件更新後觸發，確保 HTML 內容是最新的
        });
        
        // 監聽日期格式更新事件
        readyEditor.on('date-format-updated', (data) => {
          console.log('📅 日期格式已更新:', data);
          // 觸發重新渲染
          const html = readyEditor.getHtml();
          const css = readyEditor.getCss();
          setHtmlContent(`<style>${css}</style>${html}`);
        });
        
        console.log('✅ 編輯器事件監聽器設置完成');
      } else {
        console.error('❌ 編輯器沒有 on 方法，無法設置事件監聽器');
        console.error('❌ 編輯器類型:', typeof readyEditor);
        console.error('❌ 編輯器原型:', Object.getPrototypeOf(readyEditor));
      }
    }
  );

  // 當 initialSchema 或 editFormId 變更時同步 formId（例如列表 data 載入後才帶入 initialSchema）
  useEffect(() => {
    const nextId = initialSchema?.id ?? editFormId ?? null;
    if (nextId) {
      setFormId(nextId);
    }
  }, [initialSchema?.id, editFormId]);

  // 檢查 URL 參數並載入表單內容
  useEffect(() => {
    console.log('🔍 useEffect 檢查載入條件:', { editFormId, initialSchemaId: initialSchema?.id, initialSchemaFieldDisplaySettings: initialSchema?.fieldDisplaySettings });
    
    if (editFormId && !initialSchema?.id) {
      console.log('🔍 檢測到編輯表單 ID:', editFormId);
      loadFormContent(editFormId);
    } else if (initialSchema?.id) {
      console.log('🔍 使用 initialSchema 數據');
      // 如果有 initialSchema，也需要載入字段顯示設定
      if (initialSchema.fieldDisplaySettings) {
        try {
          const settings = JSON.parse(initialSchema.fieldDisplaySettings);
          console.log('🔍 從 initialSchema 載入字段設定:', settings);
          setFieldDisplaySettings(settings);
        } catch (error) {
          console.warn('從 initialSchema 解析字段顯示設定失敗:', error);
          setFieldDisplaySettings([]);
        }
      }
    }
  }, []);

  // 使用 useEffect 來監聽 grapesEditor 的變化，確保事件監聽器被正確設置
  useEffect(() => {
    if (grapesEditor && isEditorReady) {
      console.log(' useEffect: 編輯器已準備好，設置事件監聽器');
      
      // 處理編輯請求
      const handleEditRequest = (component) => {
        // 添加安全檢查
        if (!component || !component.get || typeof component.get !== 'function') {
          console.warn('⚠️ handleEditRequest: 組件無效或已被銷毀');
          return;
        }
        
        try {
          const tagName = component.get('tagName');
          const componentId = component.getId();
          
          console.log('📱 處理編輯組件請求:', tagName, componentId);
          
          // 確保 editFormData 已初始化
          if (!component.get('editFormData')) {
            console.log('🔧 編輯請求時初始化 editFormData');
            // 這裡我們需要調用 useGrapesJS 中的 initializeFormData 函數
            // 但由於我們在 EFormDesigner 中，我們需要手動初始化
            const attributes = component.getAttributes();
            
            if (tagName.toLowerCase() === 'textarea') {
              // 優先從 DOM 元素獲取 textContent，這是最可靠的方法
              let textareaValue = '';
              try {
                const el = component.getEl ? component.getEl() : null;
                if (el) {
                  textareaValue = el.textContent || el.innerText || '';
                  console.log('✅ 從 DOM 獲取 textarea 內容:', textareaValue);
                }
                
                // 如果 DOM 方法失敗，嘗試其他方法
                if (!textareaValue) {
                  textareaValue = component.get('content') || '';
                  console.log('⚠️ 使用備用方法獲取 textarea 內容:', textareaValue);
                }
                
                // 如果仍然為空，嘗試從組件的子組件獲取
                if (!textareaValue) {
                  try {
                    const components = component.get('components');
                    if (components && components.length > 0) {
                      // textarea 的內容通常存儲在子組件中
                      const firstChild = components[0];
                      if (firstChild && firstChild.get && typeof firstChild.get === 'function') {
                        textareaValue = firstChild.get('content') || '';
                        console.log('⚠️ 從子組件獲取 textarea 內容:', textareaValue);
                      }
                    }
                  } catch (childError) {
                    console.warn('⚠️ 獲取子組件內容時出錯:', childError);
                  }
                }
              } catch (error) {
                console.warn('⚠️ 獲取 textarea 內容時出錯:', error);
                textareaValue = component.get('content') || '';
              }
              
              const textareaData = {
                placeholder: attributes.placeholder || '',
                value: textareaValue,
                defaultValue: textareaValue, // 確保 defaultValue 和 value 一致
                name: attributes.name || '',
                required: attributes.required || false,
                disabled: attributes.disabled || false,
                readonly: attributes.readonly || false,
                rows: attributes.rows || '4',
                cols: attributes.cols || '50',
                maxlength: attributes.maxlength || '',
                minlength: attributes.minlength || ''
              };
              component.set('editFormData', textareaData);
              console.log('✅ textarea editFormData 已初始化:', textareaData);
            } else if (tagName.toLowerCase() === 'input') {
              // 優先獲取實際顯示的內容
              let inputValue = '';
              try {
                // 優先從 DOM 元素獲取 value 屬性，這是最可靠的方法
                const el = component.getEl ? component.getEl() : null;
                if (el) {
                  inputValue = el.value || el.getAttribute('value') || '';
                  console.log('✅ 從 DOM 獲取 input value:', inputValue);
                }
                
                // 如果 DOM 方法失敗，嘗試其他方法
                if (!inputValue) {
                  inputValue = component.get('content') || attributes.value || '';
                  console.log('⚠️ 使用備用方法獲取 input value:', inputValue);
                }
              } catch (error) {
                console.warn('⚠️ 獲取 input value 時出錯:', error);
                inputValue = attributes.value || '';
              }
              
              const dataInputType = attributes['data-input-type'];
              const inputType = dataInputType === 'currency' ? 'currency' : (dataInputType === 'date' ? 'date' : (attributes.type || 'text'));
              const inputData = {
                type: inputType,
                placeholder: attributes.placeholder || '',
                value: inputValue,
                name: attributes.name || '',
                required: attributes.required || false,
                disabled: attributes.disabled || false,
                readonly: attributes.readonly || false,
                maxlength: attributes.maxlength || '',
                minlength: attributes.minlength || '',
                pattern: attributes.pattern || '',
                title: attributes.title || ''
              };
              
              // 如果是日期類型，添加日期格式信息
              if (inputType === 'date') {
                inputData.dateFormat = attributes['data-date-format'] || 'YYYY-MM-DD';
              }
              
              component.set('editFormData', inputData);
              console.log('✅ input editFormData 已初始化:', inputData);
            }
          }
          
          // 使用函數式更新確保狀態正確更新
          setSelectedComponent(component);
          setEditPanelOpen(true);
          
          console.log('✅ 右側面板狀態已設置為開啟');
          console.log('🔍 當前 editPanelOpen 狀態:', true);
          console.log('🔍 當前 selectedComponent:', component);
        } catch (error) {
          console.error('❌ handleEditRequest 發生錯誤:', error);
          console.error('❌ 組件狀態:', {
            hasGet: !!component.get,
            getType: typeof component.get,
            component: component
          });
        }
      };
      
      // 監聽內容變化
      const handleComponentUpdate = (updatedComponent) => {
        console.log('🔄 useEffect: 組件更新事件觸發，重新獲取 HTML 內容');
        const html = grapesEditor.getHtml();
        const css = grapesEditor.getCss();
        const newContent = `<style>${css}</style>${html}`;
        console.log('🔄 useEffect: 新的 HTML 內容長度:', newContent.length);
        setHtmlContent(newContent);
        
        // 如果更新的組件是當前選中的組件，強制更新右側面板
        if (updatedComponent && selectedComponent) {
          try {
            const updatedId = updatedComponent.getId ? updatedComponent.getId() : null;
            const selectedId = selectedComponent.getId ? selectedComponent.getId() : null;
            
            if (updatedId && selectedId && updatedId === selectedId) {
              console.log('🔄 選中的組件已更新，強制更新右側面板');
              // 通過設置一個新的 selectedComponent 引用來觸發重新渲染
              // 使用 setTimeout 確保在組件更新完成後再觸發
              setTimeout(() => {
                // 重新獲取組件（確保是最新的）
                const currentSelected = grapesEditor.getSelected();
                if (currentSelected && currentSelected.getId() === selectedId) {
                  setSelectedComponent({ ...currentSelected }); // 創建新對象引用以觸發重新渲染
                  console.log('✅ 右側面板已強制更新');
                }
              }, 50);
            }
          } catch (error) {
            console.warn('⚠️ 檢查組件更新時出錯:', error);
          }
        }
      };
      
      // 監聽編輯器內容更新事件
      const handleEditorUpdate = () => {
        console.log('🔄 useEffect: 編輯器內容更新事件觸發');
        // 這個事件會在組件更新後觸發，確保 HTML 內容是最新的
      };
      
      // 監聽 editFormData 更新事件（當組件尺寸變化時）
      const handleEditFormDataUpdated = (updatedComponent) => {
        if (!updatedComponent || !selectedComponent) return;
        
        try {
          const updatedId = updatedComponent.getId ? updatedComponent.getId() : null;
          const selectedId = selectedComponent.getId ? selectedComponent.getId() : null;
          
          if (updatedId && selectedId && updatedId === selectedId) {
            console.log('🔄 EFormDesigner: 選中組件的 editFormData 已更新，強制刷新右側面板');
            // 強制更新 selectedComponent 狀態，觸發 ComponentEditor 重新渲染
            setTimeout(() => {
              const currentSelected = grapesEditor.getSelected();
              if (currentSelected && currentSelected.getId() === selectedId) {
                // 創建新對象引用以觸發 React 重新渲染
                setSelectedComponent(Object.assign({}, currentSelected));
                console.log('✅ EFormDesigner: 右側面板已強制更新');
              }
            }, 50);
          }
        } catch (error) {
          console.warn('⚠️ EFormDesigner 處理 editFormData 更新時出錯:', error);
        }
      };
      
      // 綁定事件監聽器
      grapesEditor.on('edit-component-requested', handleEditRequest);
      grapesEditor.on('component:update', handleComponentUpdate);
      grapesEditor.on('component:update', handleEditorUpdate);
      grapesEditor.on('component:editFormDataUpdated', handleEditFormDataUpdated);
      
      console.log('✅ useEffect: 事件監聽器設置完成');
      
      // 清理函數
      return () => {
        console.log(' useEffect: 清理事件監聽器');
        grapesEditor.off('edit-component-requested', handleEditRequest);
        grapesEditor.off('component:update', handleComponentUpdate);
        grapesEditor.off('component:update', handleEditorUpdate);
        grapesEditor.off('component:editFormDataUpdated', handleEditFormDataUpdated);
      };
    }
  }, [grapesEditor, isEditorReady]);

  // 添加調試日誌
  console.log(' EFormDesigner 渲染狀態:', {
    editPanelOpen,
    selectedComponent: !!selectedComponent,
    grapesEditor: !!grapesEditor,
    isEditorReady
  });

  // 移除原來的 useEffect，因為我們現在使用 onEditorReady 回調

  // 工具欄收合切換
  const toggleToolbar = () => {
    setIsToolbarCollapsed(!isToolbarCollapsed);
  };

  // 處理上傳成功（僅在目前無名稱或為預設「新表單」時才寫入上傳回傳的名稱）
  const handleUploadSuccess = (htmlContent, uploadedFormName, isImage = false) => {
    if (isImage) {
      // 圖片插入到當前選中的組件
      if (grapesEditor) {
        const selected = grapesEditor.getSelected();
        if (selected) {
          selected.components().add(htmlContent);
        }
      }
    } else {
      // 替換整個內容
      setHtmlContent(htmlContent);
      const defaultName = t('eformDesigner.newForm');
      if (uploadedFormName && (!formName.trim() || formName === defaultName)) {
        setFormName(uploadedFormName);
      }
      if (grapesEditor) {
        grapesEditor.setComponents(htmlContent);
      }
    }
  };

  // 處理 AI 生成成功（僅在目前無名稱或為預設「新表單」時才寫入 AI 回傳的名稱，避免蓋掉既有名稱）
  const handleAiSuccess = (htmlContent, aiFormName) => {
    // 🔍 記錄接收到的 HTML 內容（前 500 字符）
    const contentPreview = htmlContent?.length > 500 
      ? htmlContent.substring(0, 500) + '...' 
      : htmlContent;
    console.log('📥 [EFormDesigner] handleAiSuccess 接收到的 HTML 內容（前 500 字符）:', contentPreview);
    
    // 🔍 檢查是否包含 Markdown 代碼塊標記
    if (htmlContent?.includes('```')) {
      console.warn('⚠️ [EFormDesigner] handleAiSuccess 檢測到 Markdown 代碼塊標記 ```');
      const codeBlockIndex = htmlContent.indexOf('```');
      const contextBefore = codeBlockIndex > 50 
        ? htmlContent.substring(codeBlockIndex - 50, 50) 
        : htmlContent.substring(0, codeBlockIndex);
      const contextAfter = htmlContent.substring(codeBlockIndex, Math.min(100, htmlContent.length - codeBlockIndex));
      console.warn('⚠️ [EFormDesigner] 代碼塊標記上下文:', {
        before: '...' + contextBefore,
        marker: contextAfter
      });
    }
    
    setHtmlContent(htmlContent);
    const defaultName = t('eformDesigner.newForm');
    if (aiFormName && (!formName.trim() || formName === defaultName)) {
      setFormName(aiFormName);
    }
    if (grapesEditor) {
      // 🔍 記錄設置到 GrapesJS 之前的內容
      console.log('📤 [EFormDesigner] 準備設置到 GrapesJS，內容長度:', htmlContent?.length);
      grapesEditor.setComponents(htmlContent);
      console.log('✅ [EFormDesigner] 已設置到 GrapesJS');
    }
  };

  // 打開上傳模態框
  const openUploadModal = (type) => {
    setUploadType(type);
    setUploadModalVisible(true);
  };

  // 打開字段顯示設定模態框
  const openFieldDisplaySetting = () => {
    if (!htmlContent.trim()) {
      message.warning('請先設計表單內容');
      return;
    }
    setFieldDisplaySettingVisible(true);
  };

  // 匯出為完整 HTML 檔案（含所有 CSS）
  const handleExportHtml = () => {
    if (!grapesEditor) {
      message.warning(t('eformDesigner.importFailed') || '編輯器未就緒');
      return;
    }
    const html = grapesEditor.getHtml();
    const css = grapesEditor.getCss();
    const fullHtml = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formName || '表單'}</title>
  <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css">
  <style>${css}</style>
</head>
<body>
${html}
<script src="https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/js/bootstrap.bundle.min.js"><\/script>
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(formName || 'form').replace(/[<>:"/\\|?*]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('eformDesigner.exportSuccess'));
  };

  // 觸發匯入檔案選擇
  const handleImportHtmlClick = () => {
    if (!grapesEditor) {
      message.warning(t('eformDesigner.importFailed') || '編輯器未就緒');
      return;
    }
    importFileInputRef.current?.click();
  };

  // 處理匯入的 HTML 檔案
  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.html')) {
      message.error(t('eformDesigner.importFailed') || '請選擇 .html 檔案');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const content = reader.result;
        let html = '';
        let css = '';
        // 完整文件：從 body 與 style 擷取
        if (content.includes('<body')) {
          const bodyMatch = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          if (bodyMatch) html = bodyMatch[1].trim();
          const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
          let match;
          while ((match = styleRegex.exec(content)) !== null) {
            if (match[1] && !match[1].includes('bootstrap')) css += match[1];
          }
        } else {
          // 片段：<style>...</style> + 其餘為 html
          const styleMatch = content.match(/<style[^>]*>([\s\S]*?)<\/style>/);
          if (styleMatch) {
            css = styleMatch[1];
            html = content.replace(/<style[^>]*>[\s\S]*?<\/style>/g, '').trim();
          } else {
            html = content.trim();
          }
        }
        if (!html) {
          message.error(t('eformDesigner.importFailed') || '無法解析 HTML 內容');
          e.target.value = '';
          return;
        }
        grapesEditor.setComponents(html);
        if (css) grapesEditor.setStyle(css);
        const newContent = css ? `<style>${css}</style>${html}` : html;
        setHtmlContent(newContent);
        message.success(t('eformDesigner.importSuccess'));
      } catch (err) {
        console.error('Import HTML error:', err);
        message.error((t('eformDesigner.importFailed') || '匯入失敗') + ': ' + err.message);
      }
      e.target.value = '';
    };
    reader.readAsText(file, 'UTF-8');
  };

  // 處理字段顯示設定保存
  const handleFieldDisplaySettingSave = async (settings) => {
    setFieldDisplaySettings(settings);
    
    // 如果表單已經存在（編輯模式），立即保存字段設定到後端
    if (isEditing && formId) {
      try {
        const response = await fetch(`/api/eforms/${formId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formName,
            description: formDescription,
            htmlCode: htmlContent,
            status: 'A',
            RStatus: 'A',
            fieldDisplaySettings: settings.length > 0 ? JSON.stringify(settings) : null,
            updatedAt: new Date().toISOString()
          })
        });

        if (!response.ok) {
          throw new Error(`更新失敗: ${response.status}`);
        }

        message.success('字段顯示設定已保存');
      } catch (error) {
        console.error('保存字段設定失敗:', error);
        message.error('保存字段設定失敗: ' + error.message);
      }
    } else {
      message.success('字段顯示設定已保存到本地');
    }
  };

  // 保存表單
  const handleSave = async () => {
    // 在保存前，直接從編輯器獲取最新的 HTML 內容
    let latestHtmlContent = htmlContent;
    
    if (grapesEditor && isEditorReady) {
      try {
        // 強制刷新編輯器，確保所有更改已同步
        grapesEditor.refresh();
        
        // 在獲取 HTML 前，先從組件模型獲取所有 img 組件，確保它們的 src 是正確的
        const allComponents = grapesEditor.DomComponents.getComponents();
        const imgComponents = [];
        const imgComponentMap = new Map(); // 使用 Map 來存儲組件 ID 到組件的映射
        
        const traverse = (comp, depth = 0) => {
          try {
            if (!comp) return;
            
            // 檢查是否有 get 方法（組件模型的基本方法）
            if (comp.get && typeof comp.get === 'function') {
              const tagName = comp.get('tagName');
              
              // 如果是 img 組件，添加到列表
              if (tagName && tagName.toLowerCase() === 'img') {
                const compId = comp.getId ? comp.getId() : null;
                imgComponents.push(comp);
                
                if (compId) {
                  imgComponentMap.set(compId, comp);
                }
                
                // 也嘗試從 attributes 中獲取 id
                try {
                  const attrs = comp.getAttributes ? comp.getAttributes() : {};
                  if (attrs.id) {
                    imgComponentMap.set(attrs.id, comp);
                  }
                  console.log(`📸 找到 img 組件:`, { compId, attrsId: attrs.id, depth, src: attrs.src });
                } catch (attrError) {
                  console.warn('⚠️ 獲取組件屬性時出錯:', attrError);
                }
              }
              
              // 遍歷子組件（注意：有些組件可能有不同的結構）
              try {
                if (comp.components) {
                  let children = null;
                  if (typeof comp.components === 'function') {
                    children = comp.components();
                  } else if (Array.isArray(comp.components)) {
                    children = comp.components;
                  }
                  
                  if (Array.isArray(children) && children.length > 0) {
                    children.forEach(child => traverse(child, depth + 1));
                  }
                }
              } catch (childrenError) {
                // 忽略子組件遍歷錯誤，繼續遍歷
              }
            }
          } catch (error) {
            console.warn(`⚠️ 遍歷組件時出錯 (depth: ${depth}):`, error);
          }
        };
        
        try {
          // 嘗試不同的方式獲取組件
          let componentsToTraverse = allComponents;
          
          // 如果 allComponents 不是數組，嘗試其他方式
          if (!Array.isArray(allComponents)) {
            // 嘗試獲取 wrapper 組件
            const wrapper = grapesEditor.DomComponents ? grapesEditor.DomComponents.getWrapper() : null;
            if (wrapper) {
              componentsToTraverse = [wrapper];
            } else {
              componentsToTraverse = allComponents ? [allComponents] : [];
            }
          }
          
          if (Array.isArray(componentsToTraverse) && componentsToTraverse.length > 0) {
            componentsToTraverse.forEach(comp => traverse(comp));
          } else if (componentsToTraverse) {
            traverse(componentsToTraverse);
          }
        } catch (error) {
          console.warn('⚠️ 獲取組件列表時出錯:', error);
        }
        
        console.log(`📊 找到 ${imgComponents.length} 個 img 組件，Map 大小: ${imgComponentMap.size}`, {
          imgComponentIds: Array.from(imgComponentMap.keys())
        });
        
        // 檢查並修復所有 img 組件的 src - 在獲取 HTML 之前先同步
        let hasFixedImages = false;
        imgComponents.forEach((imgComp, index) => {
          try {
            const compAttributes = imgComp.getAttributes ? imgComp.getAttributes() : {};
            let compSrc = compAttributes.src || '';
            const editFormData = imgComp.get ? imgComp.get('editFormData') : null;
            
            // 優先使用 editFormData 中的 src（這是編輯器保存的最新值）
            // 只要 editFormData.src 有效且與組件屬性不同，就更新組件
            if (editFormData && editFormData.src && 
                !editFormData.src.includes('via.placeholder.com') && 
                !editFormData.src.includes('/assets/no-image.jpg') &&
                !editFormData.src.startsWith('data:image')) {
              // 如果組件的 src 與 editFormData 不同，或者組件的 src 是 placeholder/no-image/base64，則更新
              if (compSrc !== editFormData.src || 
                  compSrc.includes('via.placeholder.com') || 
                  compSrc.includes('/assets/no-image.jpg') ||
                  compSrc.startsWith('data:image')) {
                const compId = imgComp.getId ? imgComp.getId() : 'unknown';
                console.log(`🔄 修復 img 組件 ${index + 1} 的 src:`, { old: compSrc, new: editFormData.src, compId });
                
                if (imgComp.setAttributes) {
                  imgComp.setAttributes({ src: editFormData.src }, { silent: false });
                }
                const imgEl = imgComp.getEl ? imgComp.getEl() : null;
                if (imgEl) {
                  imgEl.setAttribute('src', editFormData.src);
                  imgEl.src = editFormData.src; // 同時設置 DOM 屬性
                }
                // 觸發組件更新事件，確保 HTML 同步
                if (imgComp.trigger) {
                  imgComp.trigger('change:attributes');
                  imgComp.trigger('component:update');
                }
                hasFixedImages = true;
              }
            } else if (compSrc.includes('via.placeholder.com') || 
                       compSrc.includes('/assets/no-image.jpg') ||
                       compSrc.startsWith('data:image')) {
              // 如果 editFormData 沒有有效的 src，但組件的 src 是 placeholder/no-image/base64，記錄警告
              const compId = imgComp.getId ? imgComp.getId() : 'unknown';
              const srcType = compSrc.includes('via.placeholder.com') ? 'placeholder' 
                            : compSrc.includes('/assets/no-image.jpg') ? 'no-image'
                            : 'base64';
              console.warn(`⚠️ img 組件 ${index + 1} 的 src 是 ${srcType}，但 editFormData 中沒有有效的 src`, { compId, editFormData, compSrc });
            }
          } catch (error) {
            console.warn(`⚠️ 修復 img 組件 ${index + 1} 時出錯:`, error);
          }
        });
        
        // 強制刷新編輯器，確保所有更改已同步到 HTML
        if (hasFixedImages) {
          grapesEditor.refresh();
          // 重新獲取組件列表以更新 Map
          imgComponentMap.clear();
          try {
            const newAllComponents = grapesEditor.DomComponents.getComponents();
            const newTraverse = (comp) => {
              try {
                if (comp && comp.get && typeof comp.get === 'function') {
                  const tagName = comp.get('tagName');
                  if (tagName && tagName.toLowerCase() === 'img') {
                    const compId = comp.getId ? comp.getId() : null;
                    if (compId) {
                      imgComponentMap.set(compId, comp);
                    }
                    const attrs = comp.getAttributes ? comp.getAttributes() : {};
                    if (attrs.id) {
                      imgComponentMap.set(attrs.id, comp);
                    }
                  }
                  if (comp.components && typeof comp.components === 'function') {
                    const children = comp.components();
                    if (Array.isArray(children)) {
                      children.forEach(newTraverse);
                    }
                  }
                }
              } catch (error) {
                console.warn('⚠️ 重新遍歷組件時出錯:', error);
              }
            };
            if (Array.isArray(newAllComponents)) {
              newAllComponents.forEach(newTraverse);
            } else if (newAllComponents) {
              newTraverse(newAllComponents);
            }
          } catch (error) {
            console.warn('⚠️ 重新獲取組件列表時出錯:', error);
          }
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // 在獲取 HTML 之前，先同步所有 table cell 的內容
        // 在保存前，確保所有 table cell (td/th) 的內容都已同步
        try {
          const cellComponents = [];
          const foundIds = new Set(); // 用於去重
          
          // 改進的遞歸遍歷函數：更全面地遍歷所有組件
          const traverseForCells = (comp, depth = 0, visited = new WeakSet()) => {
            try {
              if (!comp) return;
              if (depth > 50) return; // 增加深度限制
              if (visited.has(comp)) return; // 防止循環引用
              visited.add(comp);
              
              // 檢查是否為有效組件
              if (comp.get && typeof comp.get === 'function') {
                try {
                  const tagName = comp.get('tagName');
                  if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                    const compId = comp.getId ? comp.getId() : `unknown-${cellComponents.length}`;
                    if (!foundIds.has(compId)) {
                      foundIds.add(compId);
                      cellComponents.push(comp);
                      console.log(`✅ 找到 table cell 組件: ${tagName} (ID: ${compId}, depth: ${depth})`);
                    }
                  }
                } catch (e) {
                  // 忽略獲取 tagName 時的錯誤
                }
                
                // 遞歸遍歷子組件 - 改進的遍歷邏輯，確保能遍歷所有層級
                if (comp.components && typeof comp.components === 'function') {
                  try {
                    const children = comp.components();
                    
                    // 處理數組情況
                    if (Array.isArray(children)) {
                      if (depth <= 3) {
                        console.log(`🔍 遍歷 ${children.length} 個子組件 (depth: ${depth})`);
                        // 顯示前幾個組件的 tagName
                        children.slice(0, 3).forEach((child, idx) => {
                          if (child && child.get) {
                            const tag = child.get('tagName');
                            console.log(`  - 子組件 ${idx}: ${tag}`);
                          }
                        });
                      }
                      children.forEach(child => {
                        if (child && typeof child === 'object') {
                          traverseForCells(child, depth + 1, visited);
                        }
                      });
                    } 
                    // 處理單個組件對象
                    else if (children && typeof children === 'object') {
                      if (depth <= 3) {
                        const childTagName = children.get ? children.get('tagName') : 'unknown';
                        console.log(`🔍 遍歷單個子組件: ${childTagName} (depth: ${depth})`);
                      }
                      traverseForCells(children, depth + 1, visited);
                      
                      // 如果子組件也有 components 方法，繼續深入遍歷（重要：table > tbody > tr > td）
                      if (children.components && typeof children.components === 'function') {
                        try {
                          const grandChildren = children.components();
                          if (Array.isArray(grandChildren)) {
                            if (depth <= 3) {
                              console.log(`🔍 遍歷 ${grandChildren.length} 個孫組件 (depth: ${depth + 1})`);
                              // 顯示前幾個孫組件的 tagName
                              grandChildren.slice(0, 3).forEach((grandChild, idx) => {
                                if (grandChild && grandChild.get) {
                                  const tag = grandChild.get('tagName');
                                  console.log(`  - 孫組件 ${idx}: ${tag}`);
                                }
                              });
                            }
                            grandChildren.forEach(grandChild => {
                              if (grandChild && typeof grandChild === 'object') {
                                traverseForCells(grandChild, depth + 2, visited);
                              }
                            });
                          } else if (grandChildren && typeof grandChildren === 'object') {
                            if (depth <= 3) {
                              const grandTag = grandChildren.get ? grandChildren.get('tagName') : 'unknown';
                              console.log(`🔍 遍歷單個孫組件: ${grandTag} (depth: ${depth + 1})`);
                            }
                            traverseForCells(grandChildren, depth + 2, visited);
                            
                            // 繼續深入：處理曾孫組件（table > tbody > tr > td）
                            if (grandChildren.components && typeof grandChildren.components === 'function') {
                              try {
                                const greatGrandChildren = grandChildren.components();
                                if (Array.isArray(greatGrandChildren)) {
                                  if (depth <= 3) {
                                    console.log(`🔍 遍歷 ${greatGrandChildren.length} 個曾孫組件 (depth: ${depth + 2})`);
                                    greatGrandChildren.slice(0, 3).forEach((ggChild, idx) => {
                                      if (ggChild && ggChild.get) {
                                        const tag = ggChild.get('tagName');
                                        console.log(`  - 曾孫組件 ${idx}: ${tag}`);
                                      }
                                    });
                                  }
                                  greatGrandChildren.forEach(ggChild => {
                                    if (ggChild && typeof ggChild === 'object') {
                                      traverseForCells(ggChild, depth + 3, visited);
                                    }
                                  });
                                } else if (greatGrandChildren && typeof greatGrandChildren === 'object') {
                                  traverseForCells(greatGrandChildren, depth + 3, visited);
                                }
                              } catch (e) {
                                // 忽略錯誤
                              }
                            }
                          }
                        } catch (e) {
                          // 忽略錯誤
                        }
                      }
                    }
                  } catch (err) {
                    console.warn('⚠️ 遍歷子組件時出錯:', err);
                  }
                }
              }
            } catch (error) {
              console.warn('⚠️ 遍歷 cell 組件時出錯:', error);
            }
          };
          
          // 方法1: 從 wrapper 獲取（最可靠的方法）
          try {
            const wrapper = grapesEditor.getWrapper();
            console.log('🔍 嘗試從 wrapper 獲取組件:', { wrapper: !!wrapper });
            if (wrapper) {
              // 先遍歷 wrapper 本身
              traverseForCells(wrapper, 0);
              
              // 然後遍歷 wrapper 的子組件 - 使用更深入的遍歷
              if (wrapper.components && typeof wrapper.components === 'function') {
                try {
                  const wrapperChildren = wrapper.components();
                  console.log('🔍 wrapper.components() 返回:', { 
                    isArray: Array.isArray(wrapperChildren), 
                    type: typeof wrapperChildren,
                    length: Array.isArray(wrapperChildren) ? wrapperChildren.length : 'N/A',
                    hasGet: wrapperChildren && typeof wrapperChildren.get === 'function',
                    tagName: wrapperChildren && wrapperChildren.get ? wrapperChildren.get('tagName') : 'N/A',
                    hasComponents: wrapperChildren && typeof wrapperChildren.components === 'function',
                    constructor: wrapperChildren ? wrapperChildren.constructor?.name : 'N/A'
                  });
                  
                  // 處理數組情況
                  if (Array.isArray(wrapperChildren)) {
                    console.log(`🔍 遍歷 ${wrapperChildren.length} 個 wrapper 子組件`);
                    wrapperChildren.forEach((child, idx) => {
                      if (child && typeof child === 'object') {
                        if (idx < 3) {
                          const childTag = child.get ? child.get('tagName') : 'unknown';
                          console.log(`🔍 wrapper 子組件 ${idx}: ${childTag}`);
                        }
                        traverseForCells(child, 1);
                      }
                    });
                  } 
                  // 處理單個組件對象
                  else if (wrapperChildren && typeof wrapperChildren === 'object') {
                    const childTagName = wrapperChildren.get ? wrapperChildren.get('tagName') : 'unknown';
                    console.log(`🔍 wrapper 返回單個組件: ${childTagName}`);
                    traverseForCells(wrapperChildren, 1);
                    
                    // 如果這個組件也有 components 方法，繼續深入遍歷
                    if (wrapperChildren.components && typeof wrapperChildren.components === 'function') {
                      try {
                        const grandChildren = wrapperChildren.components();
                        if (Array.isArray(grandChildren)) {
                          console.log(`🔍 wrapper 子組件有 ${grandChildren.length} 個子組件`);
                          grandChildren.forEach((grandChild, idx) => {
                            if (grandChild && typeof grandChild === 'object') {
                              if (idx < 3) {
                                const grandTag = grandChild.get ? grandChild.get('tagName') : 'unknown';
                                console.log(`🔍 wrapper 孫組件 ${idx}: ${grandTag}`);
                              }
                              traverseForCells(grandChild, 2);
                            }
                          });
                        } else if (grandChildren && typeof grandChildren === 'object') {
                          const grandTag = grandChildren.get ? grandChildren.get('tagName') : 'unknown';
                          console.log(`🔍 wrapper 子組件返回單個孫組件: ${grandTag}`);
                          traverseForCells(grandChildren, 2);
                        }
                      } catch (e) {
                        console.warn('⚠️ 遍歷 wrapper 孫組件時出錯:', e);
                      }
                    }
                  }
                } catch (e) {
                  console.warn('⚠️ 獲取 wrapper 子組件時出錯:', e);
                }
              }
            }
          } catch (error) {
            console.warn('⚠️ 從 wrapper 獲取組件時出錯:', error);
          }
          
          // 方法2: 直接查找所有 table 組件，然後遍歷其內的 td/th（更直接的方法）
          if (cellComponents.length === 0) {
            try {
              console.log('🔍 方法2: 直接查找 table 組件');
              const wrapper = grapesEditor.getWrapper();
              
              // 遞歸查找所有 table 組件
              const findTables = (comp, depth = 0, visited = new WeakSet()) => {
                if (!comp || depth > 50 || visited.has(comp)) return [];
                visited.add(comp);
                
                const tables = [];
                try {
                  if (comp.get && typeof comp.get === 'function') {
                    const tagName = comp.get('tagName');
                    if (tagName && tagName.toLowerCase() === 'table') {
                      tables.push(comp);
                      console.log(`✅ 找到 table 組件 (ID: ${comp.getId ? comp.getId() : 'unknown'}, depth: ${depth})`);
                    }
                    
                    // 遞歸查找子組件中的 table
                    if (comp.components && typeof comp.components === 'function') {
                      const children = comp.components();
                      if (Array.isArray(children)) {
                        children.forEach(child => {
                          if (child && typeof child === 'object') {
                            tables.push(...findTables(child, depth + 1, visited));
                          }
                        });
                      } else if (children && typeof children === 'object') {
                        tables.push(...findTables(children, depth + 1, visited));
                      }
                    }
                  }
                } catch (e) {
                  // 忽略錯誤
                }
                return tables;
              };
              
              const tables = wrapper ? findTables(wrapper, 0) : [];
              console.log(`🔍 找到 ${tables.length} 個 table 組件`);
              
              // 遍歷每個 table 組件，查找其中的 td/th
              tables.forEach((table, tableIdx) => {
                console.log(`🔍 遍歷 table ${tableIdx + 1}`);
                if (table.components && typeof table.components === 'function') {
                  try {
                    const tableChildren = table.components();
                    if (Array.isArray(tableChildren)) {
                      console.log(`🔍 table ${tableIdx + 1} 有 ${tableChildren.length} 個直接子組件`);
                      tableChildren.forEach(child => {
                        if (child && typeof child === 'object') {
                          traverseForCells(child, 1);
                        }
                      });
                    } else if (tableChildren && typeof tableChildren === 'object') {
                      console.log(`🔍 table ${tableIdx + 1} 有單個直接子組件`);
                      traverseForCells(tableChildren, 1);
                    }
                  } catch (e) {
                    console.warn(`⚠️ 遍歷 table ${tableIdx + 1} 子組件時出錯:`, e);
                  }
                }
              });
            } catch (error) {
              console.warn('⚠️ 方法2 查找 table 組件時出錯:', error);
            }
          }
          
          // 方法3: 從 DomComponents.getComponents() 獲取（備用方法）
          if (cellComponents.length === 0) {
            try {
              const allComponentsForCells = grapesEditor.DomComponents.getComponents();
              console.log('🔍 方法3: 嘗試從 DomComponents.getComponents() 獲取組件:', { 
                isArray: Array.isArray(allComponentsForCells), 
                type: typeof allComponentsForCells,
                hasComponents: allComponentsForCells && typeof allComponentsForCells.components === 'function',
                hasGet: allComponentsForCells && typeof allComponentsForCells.get === 'function',
                tagName: allComponentsForCells && allComponentsForCells.get ? allComponentsForCells.get('tagName') : 'N/A'
              });
              
              if (Array.isArray(allComponentsForCells)) {
                console.log(`🔍 遍歷 ${allComponentsForCells.length} 個組件`);
                allComponentsForCells.forEach(comp => {
                  if (comp && typeof comp === 'object') {
                    traverseForCells(comp, 0);
                  }
                });
              } else if (allComponentsForCells && typeof allComponentsForCells === 'object') {
                traverseForCells(allComponentsForCells, 0);
                if (allComponentsForCells.components && typeof allComponentsForCells.components === 'function') {
                  try {
                    const children = allComponentsForCells.components();
                    if (Array.isArray(children)) {
                      console.log(`🔍 遍歷 ${children.length} 個子組件`);
                      children.forEach(child => {
                        if (child && typeof child === 'object') {
                          traverseForCells(child, 1);
                        }
                      });
                    } else if (children && typeof children === 'object') {
                      traverseForCells(children, 1);
                    }
                  } catch (e) {
                    console.warn('⚠️ 獲取子組件時出錯:', e);
                  }
                }
              }
            } catch (error) {
              console.warn('⚠️ 從 DomComponents.getComponents() 獲取組件時出錯:', error);
            }
          }
          
          // 方法4: 使用 GrapesJS 的 getComponent 方法（如果可用）或改進的 DOM 匹配
          if (cellComponents.length === 0) {
            try {
              const canvasEl = grapesEditor.Canvas.getFrameEl();
              if (canvasEl && canvasEl.contentDocument) {
                const doc = canvasEl.contentDocument;
                const allCells = doc.querySelectorAll('td, th');
                console.log(`🔍 從 DOM 找到 ${allCells.length} 個 table cell 元素`);
                
                // 嘗試使用 GrapesJS 的 getComponent 方法（如果存在）
                const DomComponents = grapesEditor.DomComponents;
                const hasGetComponent = DomComponents && typeof DomComponents.getComponent === 'function';
                
                if (hasGetComponent) {
                  console.log('🔍 使用 DomComponents.getComponent() 方法');
                  allCells.forEach((cellEl, index) => {
                    try {
                      const component = DomComponents.getComponent(cellEl);
                      if (component && component.get && typeof component.get === 'function') {
                        const tagName = component.get('tagName');
                        if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                          const compId = component.getId ? component.getId() : `dom-${index}`;
                          if (!foundIds.has(compId)) {
                            foundIds.add(compId);
                            cellComponents.push(component);
                            console.log(`✅ 通過 getComponent() 找到 table cell 組件: ${tagName} (ID: ${compId})`);
                          }
                        }
                      }
                    } catch (err) {
                      console.warn(`⚠️ 使用 getComponent() 處理 DOM 元素 ${index} 時出錯:`, err);
                    }
                  });
                }
                
                // 如果 getComponent 不可用或沒有找到組件，使用改進的遍歷匹配方法
                if (cellComponents.length === 0) {
                  console.log('🔍 使用改進的 DOM 元素匹配方法');
                  
                  // 方法A: 先嘗試通過 ID 直接查找（最快的方法）
                  if (allCells.length > 0 && allCells[0].id) {
                    console.log('🔍 方法A: 通過 ID 直接查找組件');
                    const allComps = grapesEditor.DomComponents.getComponents();
                    
                    // 遞歸查找所有組件並建立 ID 映射（使用多種 ID 來源）
                    const buildIdMap = (comp, idMap = {}, depth = 0, visited = new WeakSet()) => {
                      if (!comp || !comp.get || depth > 50 || visited.has(comp)) return idMap;
                      visited.add(comp);
                      
                      try {
                        const tagName = comp.get('tagName');
                        
                        // 如果是 td/th 組件，建立 ID 映射
                        if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                          // 方法1: 使用 GrapesJS 內部 ID
                          const compId = comp.getId ? comp.getId() : null;
                          if (compId) {
                            idMap[compId] = comp;
                            if (depth <= 3) {
                              console.log(`🔍 建立 ID 映射 (GrapesJS ID): ${compId} -> ${tagName}`);
                            }
                          }
                          
                          // 方法2: 使用組件屬性的 id
                          try {
                            const attrs = comp.getAttributes ? comp.getAttributes() : {};
                            const attrId = attrs.id;
                            if (attrId && attrId !== compId) {
                              idMap[attrId] = comp;
                              if (depth <= 3) {
                                console.log(`🔍 建立 ID 映射 (屬性 ID): ${attrId} -> ${tagName}`);
                              }
                            }
                          } catch (e) {
                            // 忽略錯誤
                          }
                          
                          // 方法3: 直接從 DOM 元素獲取 id（這是最重要的，因為 DOM 元素的 id 是我們要匹配的）
                          try {
                            const compEl = comp.getEl ? comp.getEl() : null;
                            if (compEl && compEl.id) {
                              const domId = compEl.id;
                              idMap[domId] = comp; // 直接使用 DOM id 作為 key
                              if (depth <= 3) {
                                console.log(`🔍 建立 ID 映射 (DOM ID): ${domId} -> ${tagName}`);
                              }
                            }
                          } catch (e) {
                            // 忽略錯誤
                          }
                        }
                        
                        // 無論當前組件是否是 td/th，都要繼續遍歷子組件（因為 td/th 可能在更深層）
                        
                        // 遞歸查找子組件 - 使用更全面的遍歷邏輯
                        if (comp.components && typeof comp.components === 'function') {
                          try {
                            const children = comp.components();
                            if (Array.isArray(children)) {
                              if (depth <= 3) {
                                console.log(`🔍 buildIdMap: 遍歷 ${children.length} 個子組件 (depth: ${depth}, parent: ${tagName || 'unknown'})`);
                              }
                              children.forEach((child, idx) => {
                                if (child && typeof child === 'object') {
                                  if (depth <= 3 && idx < 3) {
                                    const childTag = child.get ? child.get('tagName') : 'unknown';
                                    console.log(`  - 子組件 ${idx}: ${childTag}`);
                                  }
                                  buildIdMap(child, idMap, depth + 1, visited);
                                }
                              });
                            } else if (children && typeof children === 'object') {
                              if (depth <= 3) {
                                const childTag = children.get ? children.get('tagName') : 'unknown';
                                console.log(`🔍 buildIdMap: 遍歷單個子組件: ${childTag} (depth: ${depth}, parent: ${tagName || 'unknown'})`);
                              }
                              buildIdMap(children, idMap, depth + 1, visited);
                              
                              // 如果子組件也有 components 方法，繼續深入遍歷
                              if (children.components && typeof children.components === 'function') {
                                try {
                                  const grandChildren = children.components();
                                  if (Array.isArray(grandChildren)) {
                                    if (depth <= 3) {
                                      console.log(`🔍 buildIdMap: 遍歷 ${grandChildren.length} 個孫組件 (depth: ${depth + 1})`);
                                    }
                                    grandChildren.forEach((grandChild, idx) => {
                                      if (grandChild && typeof grandChild === 'object') {
                                        if (depth <= 3 && idx < 3) {
                                          const grandTag = grandChild.get ? grandChild.get('tagName') : 'unknown';
                                          console.log(`  - 孫組件 ${idx}: ${grandTag}`);
                                        }
                                        buildIdMap(grandChild, idMap, depth + 2, visited);
                                      }
                                    });
                                  } else if (grandChildren && typeof grandChildren === 'object') {
                                    if (depth <= 3) {
                                      const grandTag = grandChildren.get ? grandChildren.get('tagName') : 'unknown';
                                      console.log(`🔍 buildIdMap: 遍歷單個孫組件: ${grandTag} (depth: ${depth + 1})`);
                                    }
                                    buildIdMap(grandChildren, idMap, depth + 2, visited);
                                    
                                    // 繼續深入：處理曾孫組件（table > tbody > tr > td）
                                    if (grandChildren.components && typeof grandChildren.components === 'function') {
                                      try {
                                        const greatGrandChildren = grandChildren.components();
                                        if (Array.isArray(greatGrandChildren)) {
                                          if (depth <= 3) {
                                            console.log(`🔍 buildIdMap: 遍歷 ${greatGrandChildren.length} 個曾孫組件 (depth: ${depth + 2})`);
                                          }
                                          greatGrandChildren.forEach((ggChild, idx) => {
                                            if (ggChild && typeof ggChild === 'object') {
                                              if (depth <= 3 && idx < 3) {
                                                const ggTag = ggChild.get ? ggChild.get('tagName') : 'unknown';
                                                console.log(`  - 曾孫組件 ${idx}: ${ggTag}`);
                                              }
                                              buildIdMap(ggChild, idMap, depth + 3, visited);
                                            }
                                          });
                                        } else if (greatGrandChildren && typeof greatGrandChildren === 'object') {
                                          if (depth <= 3) {
                                            const ggTag = greatGrandChildren.get ? greatGrandChildren.get('tagName') : 'unknown';
                                            console.log(`🔍 buildIdMap: 遍歷單個曾孫組件: ${ggTag} (depth: ${depth + 2})`);
                                          }
                                          buildIdMap(greatGrandChildren, idMap, depth + 3, visited);
                                        }
                                      } catch (e) {
                                        // 忽略錯誤
                                      }
                                    }
                                  }
                                } catch (e) {
                                  // 忽略錯誤
                                }
                              }
                            }
                          } catch (e) {
                            console.warn(`⚠️ buildIdMap: 獲取子組件時出錯 (depth: ${depth}):`, e);
                          }
                        }
                      } catch (e) {
                        // 忽略錯誤
                      }
                      return idMap;
                    };
                    
                    // 從 wrapper 開始建立 ID 映射
                    const wrapper = grapesEditor.getWrapper();
                    const idMap = wrapper ? buildIdMap(wrapper, {}, 0) : {};
                    console.log(`🔍 建立了 ${Object.keys(idMap).length} 個 ID 映射`);
                    
                    // 通過 ID 直接匹配
                    allCells.forEach((cellEl, index) => {
                      try {
                        if (cellEl.id && idMap[cellEl.id]) {
                          const component = idMap[cellEl.id];
                          const compId = component.getId ? component.getId() : `dom-${index}`;
                          if (!foundIds.has(compId)) {
                            foundIds.add(compId);
                            cellComponents.push(component);
                            const tagName = component.get('tagName');
                            console.log(`✅ 通過 ID 映射找到 table cell 組件: ${tagName} (ID: ${compId}, DOM ID: ${cellEl.id})`);
                          }
                        }
                      } catch (err) {
                        console.warn(`⚠️ 通過 ID 映射處理 DOM 元素 ${index} 時出錯:`, err);
                      }
                    });
                  }
                  
                  // 方法B: 如果 ID 映射沒有找到所有組件，使用遍歷匹配
                  if (cellComponents.length < allCells.length) {
                    console.log(`🔍 方法B: 使用遍歷匹配（已找到 ${cellComponents.length}/${allCells.length}）`);
                    const allComps = grapesEditor.DomComponents.getComponents();
                    
                    // 改進的匹配函數：使用多種方法匹配 DOM 元素
                    const findComponentByEl = (comp, targetEl, depth = 0, visited = new WeakSet()) => {
                      if (!comp || !comp.get || depth > 50) return null;
                      if (visited.has(comp)) return null;
                      visited.add(comp);
                      
                      try {
                        // 方法1: 直接比較 DOM 元素引用
                        if (comp.getEl) {
                          const compEl = comp.getEl();
                          if (compEl === targetEl) {
                            const tagName = comp.get('tagName');
                            if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                              return comp;
                            }
                          }
                        }
                        
                        // 方法2: 通過 ID 匹配（優先）
                        if (targetEl.id) {
                          const compId = comp.getId ? comp.getId() : null;
                          const compAttrs = comp.getAttributes ? comp.getAttributes() : {};
                          if (compId === targetEl.id || compAttrs.id === targetEl.id) {
                            const tagName = comp.get('tagName');
                            if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                              return comp;
                            }
                          }
                        }
                        
                        // 方法3: 通過 DOM 元素的位置和內容匹配
                        if (comp.getEl) {
                          try {
                            const compEl = comp.getEl();
                            if (compEl && compEl.tagName && compEl.tagName.toLowerCase() === targetEl.tagName.toLowerCase()) {
                              // 檢查是否在同一個父元素下且位置相同
                              if (compEl.parentElement === targetEl.parentElement) {
                                const tagName = comp.get('tagName');
                                if (tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th')) {
                                  // 進一步驗證：檢查是否是同一個元素（通過比較所有屬性）
                                  let matchCount = 0;
                                  let totalAttrs = 0;
                                  for (let attr of targetEl.attributes) {
                                    totalAttrs++;
                                    if (compEl.getAttribute(attr.name) === attr.value) {
                                      matchCount++;
                                    }
                                  }
                                  // 如果大部分屬性匹配，認為是同一個元素
                                  if (totalAttrs === 0 || matchCount / totalAttrs > 0.5) {
                                    return comp;
                                  }
                                }
                              }
                            }
                          } catch (e) {
                            // 忽略錯誤
                          }
                        }
                        
                        // 遞歸查找子組件
                        if (comp.components && typeof comp.components === 'function') {
                          const children = comp.components();
                          if (Array.isArray(children)) {
                            for (const child of children) {
                              if (child && typeof child === 'object') {
                                const found = findComponentByEl(child, targetEl, depth + 1, visited);
                                if (found) return found;
                              }
                            }
                          } else if (children && typeof children === 'object') {
                            const found = findComponentByEl(children, targetEl, depth + 1, visited);
                            if (found) return found;
                          }
                        }
                      } catch (e) {
                        // 忽略錯誤，繼續查找
                      }
                      return null;
                    };
                    
                    // 對每個未匹配的 DOM 元素，遍歷所有組件找到匹配的
                    allCells.forEach((cellEl, index) => {
                      // 跳過已經通過 ID 映射找到的組件
                      if (cellEl.id && foundIds.has(cellEl.id)) {
                        return;
                      }
                      
                      try {
                        let component = null;
                        
                        // 從所有組件中查找匹配的
                        if (Array.isArray(allComps)) {
                          for (const comp of allComps) {
                            if (comp && typeof comp === 'object') {
                              component = findComponentByEl(comp, cellEl);
                              if (component) break;
                            }
                          }
                        } else if (allComps && typeof allComps === 'object') {
                          component = findComponentByEl(allComps, cellEl);
                        }
                        
                        if (component) {
                          const compId = component.getId ? component.getId() : `dom-${index}`;
                          if (!foundIds.has(compId)) {
                            foundIds.add(compId);
                            cellComponents.push(component);
                            const tagName = component.get('tagName');
                            console.log(`✅ 通過遍歷匹配找到 table cell 組件: ${tagName} (ID: ${compId})`);
                          }
                        } else {
                          // 添加更詳細的調試信息
                          console.warn(`⚠️ 無法為 DOM 元素 ${index} 找到對應的組件`, {
                            tagName: cellEl.tagName,
                            id: cellEl.id,
                            className: cellEl.className,
                            textContent: cellEl.textContent?.substring(0, 50)
                          });
                        }
                      } catch (err) {
                        console.warn(`⚠️ 處理 DOM 元素 ${index} 時出錯:`, err);
                      }
                    });
                  }
                }
              }
            } catch (error) {
              console.warn('⚠️ 從 DOM 查找 table cell 時出錯:', error);
            }
          }
          
          // 同步所有 table cell 的內容
          // 先過濾掉非 td/th 組件（防止錯誤匹配）
          const validCellComponents = cellComponents.filter(comp => {
            if (!comp || !comp.get || typeof comp.get !== 'function') return false;
            const tagName = comp.get('tagName');
            const isValid = tagName && (tagName.toLowerCase() === 'td' || tagName.toLowerCase() === 'th');
            if (!isValid) {
              console.warn(`⚠️ 過濾掉非 table cell 組件: ${tagName} (ID: ${comp.getId ? comp.getId() : 'unknown'})`);
            }
            return isValid;
          });
          
          console.log(`🔍 保存前找到 ${cellComponents.length} 個組件，其中 ${validCellComponents.length} 個是有效的 table cell 組件`);
          let hasUpdatedCells = false;
          validCellComponents.forEach((cellComp, index) => {
            try {
              const cellId = cellComp.getId ? cellComp.getId() : `unknown-${index}`;
              const tagName = cellComp.get('tagName');
              let editFormData = cellComp.get('editFormData');
              let cellContent = null;
              
              // 優先從 editFormData.content 讀取
              if (editFormData && editFormData.content !== undefined) {
                cellContent = editFormData.content;
                console.log(`🔍 table cell ${cellId}: 從 editFormData 讀取內容: "${cellContent}"`);
              } else {
                // 如果 editFormData 不存在或沒有 content，從 DOM 讀取並初始化
                const cellEl = cellComp.getEl();
                if (cellEl) {
                  const domContent = cellEl.textContent || cellEl.innerText || '';
                  cellContent = domContent;
                  
                  // 初始化 editFormData
                  if (!editFormData) {
                    editFormData = {};
                  }
                  editFormData.content = cellContent;
                  editFormData.tagName = cellComp.get('tagName');
                  cellComp.set('editFormData', editFormData);
                  console.log(`🔧 保存前初始化 table cell ${cellId} editFormData: "${cellContent}"`);
                } else {
                  console.warn(`⚠️ table cell ${cellId}: 無法獲取 DOM 元素`);
                }
              }
              
              if (cellContent !== null) {
                // 確保組件內容與 editFormData 一致
                const currentContent = cellComp.get('content') || '';
                const currentInnerHTML = cellComp.get('innerHTML') || '';
                const currentTextContent = cellComp.get('textContent') || '';
                
                console.log(`🔍 table cell ${cellId} 當前狀態:`, {
                  editFormDataContent: editFormData?.content,
                  componentContent: currentContent,
                  componentInnerHTML: currentInnerHTML,
                  componentTextContent: currentTextContent,
                  targetContent: cellContent
                });
                
                // 強制同步，即使內容看起來相同（因為 GrapesJS 內部狀態可能不同步）
                if (currentContent !== cellContent || currentInnerHTML !== cellContent || currentTextContent !== cellContent) {
                  console.log(`🔧 保存前同步 table cell ${cellId} 內容: "${currentContent}" -> "${cellContent}"`);
                  cellComp.set('content', cellContent);
                  cellComp.set('innerHTML', cellContent);
                  cellComp.set('textContent', cellContent);
                  
                  // 更新 DOM 元素 - 確保內容被正確寫入
                  const cellEl = cellComp.getEl();
                  if (cellEl) {
                    // 先清除所有子節點
                    while (cellEl.firstChild) {
                      cellEl.removeChild(cellEl.firstChild);
                    }
                    // 創建文本節點並添加
                    const textNode = document.createTextNode(cellContent);
                    cellEl.appendChild(textNode);
                    // 同時設置 textContent 和 innerHTML 以確保兼容性
                    cellEl.textContent = cellContent;
                    cellEl.innerHTML = cellContent;
                    console.log(`✅ table cell ${cellId} DOM 已更新: "${cellEl.textContent}"`);
                  } else {
                    console.warn(`⚠️ table cell ${cellId}: 無法獲取 DOM 元素進行更新`);
                  }
                  
                  // 觸發更新事件
                  if (cellComp.trigger) {
                    cellComp.trigger('change:content');
                    cellComp.trigger('change:innerHTML');
                    cellComp.trigger('change:textContent');
                    cellComp.trigger('component:update');
                  }
                  
                  hasUpdatedCells = true;
                } else {
                  console.log(`✅ table cell ${cellId} 內容已同步，無需更新`);
                }
              } else {
                console.warn(`⚠️ table cell ${cellId}: 無法確定內容`);
              }
            } catch (error) {
              console.warn(`⚠️ 保存前同步 table cell ${index} 內容時出錯:`, error);
            }
          });
          
          // 如果有更新，強制刷新編輯器並等待同步完成
          if (hasUpdatedCells) {
            console.log(`🔄 已更新 ${cellComponents.length} 個 table cell，刷新編輯器...`);
            grapesEditor.refresh();
            await new Promise(resolve => setTimeout(resolve, 150));
            console.log('✅ 已同步所有 table cell 內容，準備獲取 HTML');
          } else {
            console.log(`✅ 所有 table cell 內容已是最新，無需更新`);
          }
        } catch (error) {
          console.warn('⚠️ 保存前同步 table cell 時出錯，繼續使用原 HTML:', error);
        }
        
        // 強制從編輯器獲取最新的 HTML 和 CSS
        let html = grapesEditor.getHtml();
        const css = grapesEditor.getCss();
        
        console.log('📋 獲取到的原始 HTML 長度:', html.length);
        
        // 清理 HTML 中的 base64 圖片，確保只使用 URL
        // 在保存前，確保所有 img 組件的 src 都已更新到最新值
        // 🔧 優先使用前面已經找到的 imgComponents，避免重複遍歷
        let imgComponentsForSave = [];
        try {
          // 優先使用前面已經找到的 imgComponents（如果存在且不為空）
          if (imgComponents && Array.isArray(imgComponents) && imgComponents.length > 0) {
            imgComponentsForSave = imgComponents;
            console.log(`✅ [保存前] 使用前面已找到的 ${imgComponentsForSave.length} 個 img 組件`);
          } else {
            // 如果前面的遍歷沒有找到組件，重新遍歷
            console.log('⚠️ [保存前] 前面的遍歷沒有找到 img 組件，重新遍歷...');
            const allComponentsForSave = grapesEditor.DomComponents.getComponents();
            imgComponentsForSave = [];
            
            const traverseForSave = (comp, depth = 0) => {
              try {
                if (!comp) return;
                
                // 檢查是否有 get 方法（組件模型的基本方法）
                if (comp.get && typeof comp.get === 'function') {
                  const tagName = comp.get('tagName');
                  
                  // 如果是 img 組件，添加到列表
                  if (tagName && tagName.toLowerCase() === 'img') {
                    imgComponentsForSave.push(comp);
                    const compId = comp.getId ? comp.getId() : null;
                    const attrs = comp.getAttributes ? comp.getAttributes() : {};
                    console.log(`📸 [保存前] 找到 img 組件:`, { compId, attrsId: attrs.id, depth, src: attrs.src });
                  }
                  
                  // 遍歷子組件（注意：有些組件可能有不同的結構）
                  try {
                    if (comp.components) {
                      let children = null;
                      if (typeof comp.components === 'function') {
                        children = comp.components();
                      } else if (Array.isArray(comp.components)) {
                        children = comp.components;
                      }
                      
                      if (Array.isArray(children) && children.length > 0) {
                        children.forEach(child => traverseForSave(child, depth + 1));
                      }
                    }
                  } catch (childrenError) {
                    // 忽略子組件遍歷錯誤，繼續遍歷
                  }
                }
              } catch (error) {
                console.warn(`⚠️ [保存前] 遍歷組件時出錯 (depth: ${depth}):`, error);
              }
            };
            
            // 嘗試不同的方式獲取組件
            let componentsToTraverse = allComponentsForSave;
            
            // 如果 allComponents 不是數組，嘗試其他方式
            if (!Array.isArray(allComponentsForSave)) {
              // 嘗試獲取 wrapper 組件
              const wrapper = grapesEditor.DomComponents ? grapesEditor.DomComponents.getWrapper() : null;
              if (wrapper) {
                componentsToTraverse = [wrapper];
                console.log('🔍 [保存前] 使用 wrapper 組件進行遍歷');
              } else {
                componentsToTraverse = allComponentsForSave ? [allComponentsForSave] : [];
                console.log('🔍 [保存前] 將 allComponents 轉換為數組進行遍歷');
              }
            }
            
            if (Array.isArray(componentsToTraverse) && componentsToTraverse.length > 0) {
              componentsToTraverse.forEach(comp => traverseForSave(comp));
            } else if (componentsToTraverse) {
              traverseForSave(componentsToTraverse);
            }
            
            console.log(`📊 [保存前] 重新遍歷找到 ${imgComponentsForSave.length} 個 img 組件`);
          }
          
          // 更新所有 img 組件的 src
          imgComponentsForSave.forEach((imgComp, index) => {
            try {
              const editFormData = imgComp.get('editFormData');
              const compAttrs = imgComp.getAttributes();
              const compId = imgComp.getId ? imgComp.getId() : `img-${index}`;
              const compAttrId = compAttrs.id || '';
              const imgEl = imgComp.getEl ? imgComp.getEl() : null;
              const domId = imgEl ? (imgEl.getAttribute('id') || imgEl.id) : '';
              const domSrc = imgEl ? (imgEl.getAttribute('src') || imgEl.src) : null;
              
              console.log(`🔍 [保存前] img 組件 ${index + 1}:`, {
                compId,
                compAttrId,
                domId,
                editFormDataSrc: editFormData?.src,
                compAttrsSrc: compAttrs.src,
                domSrc: domSrc ? (domSrc.length > 100 ? domSrc.substring(0, 100) + '...' : domSrc) : null,
                hasEditFormData: !!editFormData,
                editFormDataKeys: editFormData ? Object.keys(editFormData) : []
              });
              
              // 優先使用 editFormData 中的 src，其次是組件屬性，最後是 DOM 元素
              let validSrc = null;
              
              if (editFormData && editFormData.src && 
                  !editFormData.src.includes('via.placeholder.com') && 
                  !editFormData.src.includes('/assets/no-image.jpg') &&
                  !editFormData.src.startsWith('data:image')) {
                validSrc = editFormData.src;
                console.log(`✅ 使用 editFormData.src: ${validSrc}`);
              } else if (compAttrs.src && 
                         !compAttrs.src.includes('via.placeholder.com') && 
                         !compAttrs.src.includes('/assets/no-image.jpg') &&
                         !compAttrs.src.startsWith('data:image')) {
                validSrc = compAttrs.src;
                console.log(`✅ 使用 compAttrs.src: ${validSrc}`);
              } else {
                const imgEl = imgComp.getEl ? imgComp.getEl() : null;
                const domSrc = imgEl ? (imgEl.getAttribute('src') || imgEl.src) : null;
                if (domSrc && 
                    !domSrc.includes('via.placeholder.com') && 
                    !domSrc.includes('/assets/no-image.jpg') &&
                    !domSrc.startsWith('data:image')) {
                  // 如果是絕對 URL，提取相對路徑
                  if (domSrc.startsWith('http')) {
                    try {
                      const urlObj = new URL(domSrc);
                      validSrc = urlObj.pathname;
                    } catch (e) {
                      validSrc = domSrc;
                    }
                  } else {
                    validSrc = domSrc;
                  }
                  console.log(`✅ 使用 DOM src: ${validSrc}`);
                }
              }
              
              // 如果找到有效的 src，確保所有地方都同步
              if (validSrc) {
                const currentCompSrc = compAttrs.src || '';
                const currentEditSrc = editFormData?.src || '';
                
                console.log(`🔍 [保存前] img 組件 ${index + 1} 當前狀態:`, {
                  validSrc,
                  currentCompSrc,
                  currentEditSrc,
                  needUpdateComp: currentCompSrc !== validSrc,
                  needUpdateEdit: currentEditSrc !== validSrc
                });
                
                // 如果組件屬性的 src 與有效 src 不同，更新它
                if (currentCompSrc !== validSrc) {
                  console.log(`🔧 [保存前] 更新 img 組件 ${index + 1} 組件屬性 src: ${currentCompSrc || '(空)'} -> ${validSrc}`);
                  // 更新組件屬性（觸發更新事件）
                  if (imgComp.setAttributes) {
                    imgComp.setAttributes({ src: validSrc }, { silent: false });
                    // 驗證更新是否成功
                    const updatedAttrs = imgComp.getAttributes();
                    console.log(`✅ [保存前] 組件屬性已更新，驗證: ${updatedAttrs.src === validSrc ? '成功' : '失敗'}`, {
                      expected: validSrc,
                      actual: updatedAttrs.src
                    });
                  }
                } else {
                  console.log(`✅ [保存前] img 組件 ${index + 1} 組件屬性 src 已是最新: ${validSrc}`);
                }
                
                // 如果 editFormData 的 src 與有效 src 不同，更新它
                if (currentEditSrc !== validSrc) {
                  if (imgComp.set && typeof imgComp.set === 'function') {
                    const currentEditFormData = imgComp.get('editFormData') || {};
                    currentEditFormData.src = validSrc;
                    imgComp.set('editFormData', currentEditFormData);
                    console.log(`🔧 [保存前] 更新 editFormData.src: ${currentEditSrc || '(空)'} -> ${validSrc}`);
                    // 驗證更新是否成功
                    const updatedEditFormData = imgComp.get('editFormData');
                    console.log(`✅ [保存前] editFormData 已更新，驗證: ${updatedEditFormData?.src === validSrc ? '成功' : '失敗'}`, {
                      expected: validSrc,
                      actual: updatedEditFormData?.src
                    });
                  }
                } else {
                  console.log(`✅ [保存前] img 組件 ${index + 1} editFormData.src 已是最新: ${validSrc}`);
                }
                
                // 同時更新 DOM 元素
                const imgElForUpdate = imgComp.getEl ? imgComp.getEl() : null;
                if (imgElForUpdate) {
                  const absoluteSrc = validSrc.startsWith('/') 
                    ? `${window.location.origin}${validSrc}`
                    : validSrc;
                  const oldDomSrc = imgElForUpdate.getAttribute('src') || imgElForUpdate.src;
                  imgElForUpdate.src = absoluteSrc;
                  imgElForUpdate.setAttribute('src', validSrc);
                  console.log(`🔧 [保存前] 更新 DOM src: ${oldDomSrc || '(空)'} -> ${validSrc}`);
                  // 驗證更新是否成功
                  const updatedDomSrc = imgElForUpdate.getAttribute('src') || imgElForUpdate.src;
                  console.log(`✅ [保存前] DOM src 已更新，驗證: ${updatedDomSrc === validSrc || updatedDomSrc === absoluteSrc ? '成功' : '失敗'}`, {
                    expected: validSrc,
                    actual: updatedDomSrc
                  });
                }
                
                // 觸發組件更新事件，確保 HTML 同步
                if (imgComp.trigger) {
                  imgComp.trigger('change:attributes');
                  imgComp.trigger('component:update');
                  console.log(`✅ [保存前] img 組件 ${index + 1} 已觸發更新事件`);
                }
              } else {
                console.warn(`⚠️ [保存前] img 組件 ${index + 1} 沒有找到有效的 src`);
              }
            } catch (error) {
              console.warn('⚠️ 保存前更新 img src 時出錯:', error);
            }
          });
          
          // 如果有更新，強制刷新編輯器並重新獲取 HTML
          if (imgComponentsForSave.length > 0) {
            console.log(`🔄 [保存前] 刷新編輯器並重新獲取 HTML，共 ${imgComponentsForSave.length} 個 img 組件`);
            grapesEditor.refresh();
            // 等待一小段時間，確保更新已應用
            await new Promise(resolve => setTimeout(resolve, 100));
            html = grapesEditor.getHtml();
            console.log('🔄 [保存前] 更新 img 組件後重新獲取 HTML，長度:', html.length);
            
            // 驗證更新後的 HTML 中的 img src
            const verifyDiv = document.createElement('div');
            verifyDiv.innerHTML = html;
            const verifyImgs = verifyDiv.querySelectorAll('img');
            console.log(`🔍 [保存前] 驗證更新後的 HTML 中的 ${verifyImgs.length} 個 img 標籤:`);
            verifyImgs.forEach((img, idx) => {
              const imgId = img.getAttribute('id') || '';
              const imgSrc = img.getAttribute('src') || img.src || '';
              console.log(`  img ${idx + 1} (id: ${imgId}):`, {
                src: imgSrc.length > 100 ? imgSrc.substring(0, 100) + '...' : imgSrc,
                // 查找對應的組件
                matchingComp: imgComponentsForSave.find(comp => {
                  const compId = comp.getId ? comp.getId() : '';
                  const compAttrId = comp.getAttributes()?.id || '';
                  const compEl = comp.getEl ? comp.getEl() : null;
                  const compDomId = compEl ? (compEl.getAttribute('id') || compEl.id) : '';
                  return compId === imgId || compAttrId === imgId || compDomId === imgId;
                }) ? '找到' : '未找到'
              });
            });
          }
        } catch (error) {
          console.warn('⚠️ 保存前更新 img 組件時出錯，繼續使用原 HTML:', error);
        }
        
        // 查找所有 img 標籤，檢查 src 是否為 base64
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const imgTags = tempDiv.querySelectorAll('img');
        
        console.log(`🔍 [保存時] 從 HTML 中找到 ${imgTags.length} 個 img 標籤`);
        
        let base64Count = 0;
        imgTags.forEach((img, index) => {
          const src = img.getAttribute('src');
          const imgId = img.getAttribute('id') || '';
          
          // 🔧 嘗試通過 DOM 元素找到對應的組件並更新 src
          let matchingComp = null;
          let updatedSrc = null;
          
          // 方法1: 通過 imgComponentsForSave 查找
          if (imgComponentsForSave && imgComponentsForSave.length > 0) {
            matchingComp = imgComponentsForSave.find(comp => {
              try {
                const compId = comp.getId ? comp.getId() : '';
                const compAttrId = comp.getAttributes()?.id || '';
                const compEl = comp.getEl ? comp.getEl() : null;
                const compDomId = compEl ? (compEl.getAttribute('id') || compEl.id) : '';
                return compId === imgId || compAttrId === imgId || compDomId === imgId;
              } catch (e) {
                return false;
              }
            });
          }
          
          // 方法2: 如果 imgComponentsForSave 沒有找到，嘗試通過 DOM 元素直接查找組件
          if (!matchingComp && imgId) {
            try {
              const canvasEl = grapesEditor.Canvas.getFrameEl();
              if (canvasEl && canvasEl.contentDocument) {
                const doc = canvasEl.contentDocument;
                const domImg = doc.getElementById(imgId);
                if (domImg) {
                  matchingComp = grapesEditor.DomComponents.getComponent(domImg);
                  if (matchingComp) {
                    console.log(`✅ [保存時] 通過 DOM 元素找到組件 (id: ${imgId})`);
                  }
                }
              }
            } catch (e) {
              console.warn(`⚠️ [保存時] 通過 DOM 查找組件時出錯:`, e);
            }
          }
          
          // 方法3: 如果還是找不到，嘗試通過 imgComponentMap 查找
          if (!matchingComp && imgId && imgComponentMap && imgComponentMap.has(imgId)) {
            matchingComp = imgComponentMap.get(imgId);
            console.log(`✅ [保存時] 通過 Map 找到組件 (id: ${imgId})`);
          }
          
          // 如果找到組件，嘗試從 editFormData 或屬性獲取最新的 src
          if (matchingComp) {
            try {
              // 優先從 editFormData 獲取（這是編輯器保存的最新值）
              const editFormData = matchingComp.get ? matchingComp.get('editFormData') : null;
              console.log(`🔍 [保存時] 檢查組件數據 (id: ${imgId}):`, {
                hasEditFormData: !!editFormData,
                editFormDataSrc: editFormData?.src || '無',
                hasGetAttributes: typeof matchingComp.getAttributes === 'function'
              });
              
              if (editFormData && editFormData.src && 
                  !editFormData.src.startsWith('data:image') &&
                  !editFormData.src.includes('via.placeholder.com') && 
                  !editFormData.src.includes('/assets/no-image.jpg')) {
                updatedSrc = editFormData.src;
                console.log(`✅ [保存時] 從 editFormData 獲取最新 src (id: ${imgId}):`, updatedSrc);
              } else {
                // 如果 editFormData 沒有，從組件屬性獲取
                const compAttrs = matchingComp.getAttributes ? matchingComp.getAttributes() : {};
                console.log(`🔍 [保存時] 檢查組件屬性 (id: ${imgId}):`, {
                  attrsSrc: compAttrs.src || '無',
                  allAttrs: Object.keys(compAttrs)
                });
                
                if (compAttrs.src && 
                    !compAttrs.src.startsWith('data:image') &&
                    !compAttrs.src.includes('via.placeholder.com') && 
                    !compAttrs.src.includes('/assets/no-image.jpg')) {
                  updatedSrc = compAttrs.src;
                  console.log(`✅ [保存時] 從組件屬性獲取最新 src (id: ${imgId}):`, updatedSrc);
                }
              }
              
              // 如果從組件沒有獲取到新值，嘗試從 Canvas DOM 獲取（作為備用）
              if (!updatedSrc && imgId) {
                try {
                  const canvasEl = grapesEditor.Canvas.getFrameEl();
                  if (canvasEl && canvasEl.contentDocument) {
                    const doc = canvasEl.contentDocument;
                    const domImg = doc.getElementById(imgId);
                    if (domImg) {
                      const canvasSrc = domImg.getAttribute('src') || domImg.src;
                      // 提取相對路徑（移除可能的完整 URL）
                      const relativeSrc = canvasSrc.includes('/Uploads/') 
                        ? canvasSrc.substring(canvasSrc.indexOf('/Uploads/'))
                        : canvasSrc;
                      
                      if (relativeSrc && 
                          !relativeSrc.startsWith('data:image') &&
                          !relativeSrc.includes('via.placeholder.com') && 
                          !relativeSrc.includes('/assets/no-image.jpg') &&
                          relativeSrc !== src) {
                        updatedSrc = relativeSrc;
                        console.log(`✅ [保存時] 從 Canvas DOM 獲取最新 src (id: ${imgId}):`, updatedSrc);
                      }
                    }
                  }
                } catch (e) {
                  console.warn(`⚠️ [保存時] 從 Canvas DOM 獲取 src 時出錯 (id: ${imgId}):`, e);
                }
              }
              
              // 如果找到了新的 src 且與當前不同，更新 HTML 中的 img 標籤
              if (updatedSrc && updatedSrc !== src) {
                img.setAttribute('src', updatedSrc);
                console.log(`🔄 [保存時] 更新 HTML 中的 img src (id: ${imgId}):`, { 
                  old: src ? (src.length > 50 ? src.substring(0, 50) + '...' : src) : '無', 
                  new: updatedSrc 
                });
              } else if (!updatedSrc) {
                console.warn(`⚠️ [保存時] 無法獲取組件的最新 src (id: ${imgId}):`, {
                  editFormDataSrc: editFormData?.src || '無',
                  compAttrsSrc: matchingComp.getAttributes?.()?.src || '無',
                  htmlSrc: src || '無'
                });
              }
            } catch (e) {
              console.warn(`⚠️ [保存時] 獲取組件 src 時出錯 (id: ${imgId}):`, e);
            }
          } else if (imgId) {
            // 備用方案：如果找不到組件，直接從 Canvas DOM 中獲取最新的 src
            try {
              const canvasEl = grapesEditor.Canvas.getFrameEl();
              if (canvasEl && canvasEl.contentDocument) {
                const doc = canvasEl.contentDocument;
                const domImg = doc.getElementById(imgId);
                if (domImg) {
                  const canvasSrc = domImg.getAttribute('src') || domImg.src;
                  // 提取相對路徑（移除可能的完整 URL）
                  const relativeSrc = canvasSrc.includes('/Uploads/') 
                    ? canvasSrc.substring(canvasSrc.indexOf('/Uploads/'))
                    : canvasSrc;
                  
                  if (relativeSrc && 
                      !relativeSrc.startsWith('data:image') &&
                      !relativeSrc.includes('via.placeholder.com') && 
                      !relativeSrc.includes('/assets/no-image.jpg') &&
                      relativeSrc !== src) {
                    updatedSrc = relativeSrc;
                    img.setAttribute('src', updatedSrc);
                    console.log(`🔄 [保存時] 從 Canvas DOM 更新 HTML 中的 img src (id: ${imgId}):`, { 
                      old: src ? (src.length > 50 ? src.substring(0, 50) + '...' : src) : '無', 
                      new: updatedSrc 
                    });
                  }
                }
              }
            } catch (e) {
              console.warn(`⚠️ [保存時] 從 Canvas DOM 獲取 src 時出錯 (id: ${imgId}):`, e);
            }
          }
          
          // 讀取 DOM 上當前的 src（可能已在上方被更新為 PNG URL）
          const currentSrc = img.getAttribute('src');
          
          console.log(`🖼️ [保存時] 檢查圖片 ${index + 1} (id: ${imgId}):`, {
            originalSrc: src ? (src.length > 100 ? src.substring(0, 100) + '...' : src) : '無 src',
            currentSrc: currentSrc ? (currentSrc.length > 100 ? currentSrc.substring(0, 100) + '...' : currentSrc) : '無 src',
            updatedSrc: updatedSrc || '未更新',
            alt: img.getAttribute('alt'),
            width: img.getAttribute('width'),
            height: img.getAttribute('height'),
            style: img.getAttribute('style'),
            matchingComp: matchingComp ? '找到' : '未找到'
          });
          
          // 使用 currentSrc（更新後的值）而非原始 src 變量，避免誤清除已更新的 PNG URL
          if (currentSrc && currentSrc.startsWith('data:image')) {
            base64Count++;
            console.warn('⚠️ 檢測到 base64 格式的圖片，清除它:', currentSrc.substring(0, 50));
            // 清除 base64，保留其他屬性
            img.removeAttribute('src');
            // 添加一個 placeholder，提示需要上傳圖片
            img.setAttribute('alt', img.getAttribute('alt') || '請上傳圖片');
            img.setAttribute('data-base64-removed', 'true');
          }
          
          // 檢查是否為默認的 placeholder 或 no-image 圖片，如果是則從組件模型獲取正確的 src
          if (src && (src.includes('via.placeholder.com') || src.includes('/assets/no-image.jpg'))) {
            console.warn(`⚠️ 檢測到默認 placeholder/no-image 圖片，位置 ${index + 1}，嘗試從組件模型修復`, { src });
            
            let correctSrc = null;
            const imgId = img.getAttribute('id');
            
            // 首先嘗試通過 Map 查找組件（更快更可靠）
            let matchingComp = null;
            if (imgId && imgComponentMap.has(imgId)) {
              matchingComp = imgComponentMap.get(imgId);
              console.log(`✅ 通過 Map 找到組件:`, { htmlId: imgId, compId: matchingComp.getId ? matchingComp.getId() : 'unknown' });
            }
            
            // 如果 Map 找不到，嘗試通過遍歷查找
            if (!matchingComp && imgId) {
              for (const comp of imgComponents) {
                try {
                  const compId = comp.getId ? comp.getId() : null;
                  const attrs = comp.getAttributes ? comp.getAttributes() : {};
                  if (compId === imgId || attrs.id === imgId || compId === imgId.replace(/^i/, '')) {
                    matchingComp = comp;
                    console.log(`✅ 通過遍歷找到組件:`, { htmlId: imgId, compId });
                    break;
                  }
                } catch (error) {
                  // 忽略錯誤，繼續查找
                }
              }
            }
            
            // 如果還是找不到，且只有一個 img 組件，使用它
            if (!matchingComp && imgComponents.length === 1 && imgTags.length === 1) {
              matchingComp = imgComponents[0];
              console.log(`🔄 使用索引匹配組件（ID 匹配失敗）`);
            }
            
            if (matchingComp) {
              try {
                // 優先從 editFormData 獲取（這是編輯器保存的最新值）
                const editFormData = matchingComp.get ? matchingComp.get('editFormData') : null;
                
                if (editFormData && editFormData.src && 
                    !editFormData.src.includes('via.placeholder.com') && 
                    !editFormData.src.includes('/assets/no-image.jpg') &&
                    !editFormData.src.startsWith('data:image')) {
                  correctSrc = editFormData.src;
                  console.log(`✅ 從 editFormData 修復 img src:`, correctSrc, { htmlId: imgId });
                } else {
                  // 如果 editFormData 沒有，從組件屬性獲取
                  const compSrc = matchingComp.getAttributes ? matchingComp.getAttributes().src : null;
                  if (compSrc && 
                      !compSrc.includes('via.placeholder.com') && 
                      !compSrc.includes('/assets/no-image.jpg') &&
                      !compSrc.startsWith('data:image')) {
                    correctSrc = compSrc;
                    console.log(`✅ 從組件屬性修復 img src:`, correctSrc, { htmlId: imgId });
                  }
                }
              } catch (error) {
                console.warn(`⚠️ 從組件模型獲取 src 時出錯:`, error);
              }
            }
            
            // 如果找到了正確的 src，更新 HTML 元素
            if (correctSrc) {
              img.setAttribute('src', correctSrc);
              console.log(`✅ HTML img 元素的 src 已更新為:`, correctSrc);
            } else {
              // 如果無法匹配組件，嘗試從所有 imgComponents 中找到任何有有效 src 的組件
              for (const comp of imgComponents) {
                try {
                  const editFormData = comp.get ? comp.get('editFormData') : null;
                  let validSrc = null;
                  
                  if (editFormData && editFormData.src && 
                      !editFormData.src.includes('via.placeholder.com') && 
                      !editFormData.src.includes('/assets/no-image.jpg') &&
                      !editFormData.src.startsWith('data:image')) {
                    validSrc = editFormData.src;
                  } else {
                    const compSrc = comp.getAttributes ? comp.getAttributes().src : null;
                    if (compSrc && 
                        !compSrc.includes('via.placeholder.com') && 
                        !compSrc.includes('/assets/no-image.jpg') &&
                        !compSrc.startsWith('data:image')) {
                      validSrc = compSrc;
                    }
                  }
                  
                  if (validSrc) {
                    correctSrc = validSrc;
                    img.setAttribute('src', correctSrc);
                    console.log(`✅ 使用備用方法修復 img src:`, correctSrc);
                    break;
                  }
                } catch (error) {
                  // 忽略錯誤，繼續查找
                }
              }
              
              if (!correctSrc) {
                // 最後備用方案：直接從 Canvas DOM 中查找圖片元素
                try {
                  const canvasEl = grapesEditor.Canvas ? grapesEditor.Canvas.getDocument() : null;
                  if (canvasEl) {
                    let canvasImg = null;
                    if (imgId) {
                      // 通過 ID 查找
                      canvasImg = canvasEl.querySelector(`img#${imgId}`);
                    }
                    
                    if (!canvasImg) {
                      // 如果 ID 找不到，通過索引查找（如果只有一個 img）
                      const allCanvasImgs = canvasEl.querySelectorAll('img');
                      if (allCanvasImgs.length === 1 && imgTags.length === 1) {
                        canvasImg = allCanvasImgs[0];
                      } else if (allCanvasImgs.length > 0 && index < allCanvasImgs.length) {
                        // 通過位置索引匹配
                        canvasImg = allCanvasImgs[index];
                      }
                    }
                    
                    if (canvasImg) {
                      const canvasSrc = canvasImg.getAttribute('src') || canvasImg.src;
                      if (canvasSrc && !canvasSrc.includes('via.placeholder.com') && !canvasSrc.startsWith('data:image')) {
                        correctSrc = canvasSrc;
                        img.setAttribute('src', correctSrc);
                        console.log(`✅ 從 Canvas DOM 修復 img src:`, correctSrc, { htmlId: imgId, canvasSrc });
                      }
                    }
                  }
                } catch (canvasError) {
                  console.warn(`⚠️ 從 Canvas DOM 查找圖片時出錯:`, canvasError);
                }
                
                if (!correctSrc) {
                  console.warn(`⚠️ 無法修復 placeholder 圖片`, { 
                    htmlId: imgId, 
                    imgComponentsCount: imgComponents.length,
                    imgComponentIds: imgComponents.map(c => {
                      try {
                        return c.getId ? c.getId() : 'unknown';
                      } catch {
                        return 'error';
                      }
                    })
                  });
                }
              }
            }
          }
        });
        
        // 無論是否有 base64 圖片被清除，都要更新 html（因為可能修復了 placeholder）
        html = tempDiv.innerHTML;
        
        // 在保存前，再次確保所有 table cell 的內容都正確寫入到 HTML 中
        try {
          const allCellElements = tempDiv.querySelectorAll('td, th');
          allCellElements.forEach((cellEl, index) => {
            try {
              // 嘗試從組件模型獲取正確的內容
              const cellId = cellEl.getAttribute('id');
              if (cellId) {
                // 遍歷所有組件找到對應的 cell 組件
                const allComponentsForFinalCheck = grapesEditor.DomComponents.getComponents();
                const findCellComponent = (comp) => {
                  try {
                    if (comp && comp.get && typeof comp.get === 'function') {
                      const compId = comp.getId ? comp.getId() : null;
                      const compTagName = comp.get('tagName');
                      if (compId === cellId && (compTagName === 'td' || compTagName === 'th')) {
                        return comp;
                      }
                      if (comp.components && typeof comp.components === 'function') {
                        const children = comp.components();
                        if (Array.isArray(children)) {
                          for (const child of children) {
                            const found = findCellComponent(child);
                            if (found) return found;
                          }
                        }
                      }
                    }
                  } catch (error) {
                    // 忽略錯誤
                  }
                  return null;
                };
                
                let cellComp = null;
                if (Array.isArray(allComponentsForFinalCheck)) {
                  for (const comp of allComponentsForFinalCheck) {
                    cellComp = findCellComponent(comp);
                    if (cellComp) break;
                  }
                } else if (allComponentsForFinalCheck) {
                  cellComp = findCellComponent(allComponentsForFinalCheck);
                }
                
                if (cellComp) {
                  const editFormData = cellComp.get('editFormData');
                  // 優先從 editFormData.content 讀取，這是最可靠的數據源
                  const correctContent = editFormData && editFormData.content !== undefined 
                    ? editFormData.content 
                    : (cellComp.get('content') || '');
                  
                  const currentHtmlContent = cellEl.textContent || cellEl.innerText || '';
                  if (correctContent !== currentHtmlContent) {
                    console.log(`🔧 最終檢查：修復 table cell ${index + 1} 內容: "${currentHtmlContent}" -> "${correctContent}"`, {
                      editFormDataContent: editFormData?.content,
                      componentContent: cellComp.get('content'),
                      domContent: currentHtmlContent
                    });
                    // 清除所有子節點
                    while (cellEl.firstChild) {
                      cellEl.removeChild(cellEl.firstChild);
                    }
                    // 創建文本節點並添加
                    const textNode = document.createTextNode(correctContent);
                    cellEl.appendChild(textNode);
                    cellEl.textContent = correctContent;
                    cellEl.innerHTML = correctContent;
                  }
                }
              }
            } catch (error) {
              console.warn(`⚠️ 最終檢查 table cell ${index + 1} 時出錯:`, error);
            }
          });
          
          // 更新 HTML
          html = tempDiv.innerHTML;
        } catch (error) {
          console.warn('⚠️ 最終檢查 table cell 時出錯，繼續使用原 HTML:', error);
        }
        
        if (base64Count > 0) {
          console.log(`🔄 已清除 ${base64Count} 個 base64 格式的圖片`);
          message.warning(`已清除 ${base64Count} 個 base64 格式的圖片，請使用 URL 格式的圖片`);
        }
        
        // 驗證保存的 HTML 中是否包含正確的圖片 URL
        console.log('🔍 保存前的 HTML 內容中所有 img 標籤:', tempDiv.querySelectorAll('img').length);
        tempDiv.querySelectorAll('img').forEach((img, index) => {
          console.log(`  img ${index + 1} src:`, img.getAttribute('src'));
        });
        
        latestHtmlContent = `<style>${css}</style>${html}`;
        console.log('💾 保存時從編輯器獲取最新內容，長度:', latestHtmlContent.length);
        
        // 同時更新 state，保持同步
        setHtmlContent(latestHtmlContent);
      } catch (error) {
        console.warn('⚠️ 從編輯器獲取內容時出錯，使用 state 中的內容:', error);
        // 如果出錯，使用 state 中的內容作為備用
      }
    }
    
    if (!latestHtmlContent.trim()) {
      message.warning('請先設計表單內容');
      return;
    }

    setIsSaving(true);
    try {
      const formData = {
        name: formName,
        description: formDescription,
        htmlCode: latestHtmlContent, // 使用從編輯器獲取的最新內容
        status: 'A', // Active
        RStatus: 'A', // Active - 修正字段名以匹配後端模型
        fieldDisplaySettings: fieldDisplaySettings.length > 0 ? JSON.stringify(fieldDisplaySettings) : null
      };

      // 如果是編輯模式，添加 updatedAt
      if (isEditing) {
        formData.updatedAt = new Date().toISOString();
      }

      console.log('📤 發送保存請求:', formData);

      const token = localStorage.getItem('token');
      const url = isEditing ? `/api/eforms/${formId}` : '/api/eforms';
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      console.log('📥 收到響應:', response.status, response.statusText);

      // 檢查響應狀態
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // 檢查響應內容類型
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ 非 JSON 響應:', text);
        throw new Error('服務器返回非 JSON 格式響應');
      }

      const result = await response.json();
      console.log('📥 解析響應:', result);

      // 後端直接返回表單對象，沒有 success 字段
      if (result && result.id) {
        // 更新編輯狀態和表單ID
        setIsEditing(true);
        setFormId(result.id);
        
        message.success('✅ 表單保存成功！');
        // 只調用 onSave 回調，不傳遞數據，避免重複保存
        onSave && onSave();
      } else {
        message.error('❌ 保存失敗: 響應格式錯誤');
      }
    } catch (error) {
      console.error('❌ 保存錯誤:', error);
      
      if (error.name === 'SyntaxError') {
        message.error('❌ 服務器響應格式錯誤，請檢查網絡連接');
      } else if (error.message.includes('Failed to fetch')) {
        message.error('❌ 網絡連接失敗，請檢查網絡設置');
      } else {
        message.error('❌ 保存失敗: ' + error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // 如果正在載入表單內容，顯示載入狀態
  if (isLoadingForm) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ fontSize: '18px', color: '#666' }}>載入表單內容中...</div>
        <div style={{ fontSize: '14px', color: '#999' }}>請稍候</div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 樣式 */}
      <style>{purpleButtonStyle}</style>
      
      {/* 頂部工具欄 */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #e8e8e8', 
        backgroundColor: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            className="purple-back-button"
            style={{ 
              backgroundColor: '#722ed1', 
              borderColor: '#722ed1',
              color: 'white',
              height: '32px',
              width: '32px',
              padding: '0'
            }}
          />
          <Button
            icon={<SettingOutlined />}
            type="default"
            onClick={openFieldDisplaySetting}
            disabled={!htmlContent.trim()}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
            title="字段顯示設定"
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportHtml}
            disabled={!isEditorReady || !grapesEditor}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
            title={t('eformDesigner.exportHtml')}
          />
          <Button
            icon={<UploadOutlined />}
            onClick={handleImportHtmlClick}
            disabled={!isEditorReady || !grapesEditor}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
            title={t('eformDesigner.importHtml')}
          />
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={handleSave}
            loading={isSaving}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
          />
        </div>
        
        <Title level={4} style={{ margin: 0 }}>{t('eformDesigner.title')}</Title>
      </div>

      {/* 主要內容區域 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* 左側工具欄 */}
        <div style={{ 
          width: isToolbarCollapsed ? '0px' : '280px', 
          borderRight: isToolbarCollapsed ? 'none' : '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          opacity: isToolbarCollapsed ? 0 : 1,
          visibility: isToolbarCollapsed ? 'hidden' : 'visible',
          paddingLeft: 5,
          paddingRight: 5
        }}>
          <Tabs
            className="eform-designer-side-tabs"
            defaultActiveKey="basic"
            size="small"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            items={[
              {
                key: 'basic',
                label: t('eformDesigner.tabBasicInfo') || '基本資訊',
                children: (
                  <div style={{ padding: '12px 12px 12px 0', overflowY: 'auto', height: '100%' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <label>{t('eformDesigner.formName')}:</label>
                      <Input
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder={t('eformDesigner.formNamePlaceholder')}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label>{t('eformDesigner.formDescription')}:</label>
                      <Input.TextArea
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                        placeholder={t('eformDesigner.formDescriptionPlaceholder')}
                        rows={4}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                  </div>
                )
              },
              {
                key: 'upload',
                label: t('eformDesigner.tabFileUpload') || '文件上傳',
                children: (
                  <div style={{ padding: '12px 12px 12px 0', overflowY: 'auto', height: '100%' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button onClick={() => openUploadModal('word')} style={{ width: '100%' }}>
                        📄 {t('eformDesigner.createFromWordFile')}
                      </Button>
                      <Button onClick={() => openUploadModal('excel')} style={{ width: '100%' }}>
                        📊 {t('eformDesigner.createFromExcelFile')}
                      </Button>
                      <Button onClick={() => openUploadModal('pdf')} style={{ width: '100%' }}>
                        📑 {t('eformDesigner.createFromPdfFile')}
                      </Button>
                    </Space>
                  </div>
                )
              },
              {
                key: 'ai',
                label: t('eformDesigner.tabAIAssistant') || 'AI 表單助理',
                children: (
                  <div style={{ padding: 0, overflow: 'hidden', height: '100%', minHeight: 0 }}>
                    <EFormDesignerAIChat
                      eFormDefinitionId={formId || null}
                      htmlContent={htmlContent}
                      onSuccess={handleAiSuccess}
                      isGenerating={isAiGenerating}
                      setIsGenerating={setIsAiGenerating}
                    />
                  </div>
                )
              }
            ]}
          />
        </div>

        {/* 匯入用隱藏檔案 input */}
        <input
          ref={importFileInputRef}
          type="file"
          accept=".html"
          style={{ display: 'none' }}
          onChange={handleImportFileChange}
        />

        {/* 工具欄切換按鈕 */}
        <div style={{
          position: 'absolute',
          left: isToolbarCollapsed ? '0px' : '280px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          transition: 'left 0.3s ease'
        }}>
          <Button
            icon={isToolbarCollapsed ? <UpOutlined /> : <UpOutlined rotate={180} />}
            onClick={toggleToolbar}
            type="primary"
            size="small"
            style={{
              borderRadius: isToolbarCollapsed ? '0 4px 4px 0' : '4px 0 0 4px',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
              border: '1px solid #e8e8e8',
              borderLeft: isToolbarCollapsed ? 'none' : '1px solid #e8e8e8',
              borderRight: isToolbarCollapsed ? '1px solid #e8e8e8' : 'none'
            }}
          />
        </div>

        {/* 編輯器區域 - 調整寬度以適應右側面板 */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          width: editPanelOpen ? 'calc(100% - 420px)' : '100%',
          transition: 'width 0.3s ease'
        }}>
          <AIGeneratingOverlay visible={isAiGenerating} t={t} />
          <div
            ref={editorRef}
            style={{ 
              height: '100%', 
              width: '100%',
              backgroundColor: '#f5f5f5'
            }}
          />
        </div>

        {/* 右側編輯面板 - 替換提示信息為實際編輯器 */}
        {editPanelOpen && (
          <div style={{
            width: '420px',
            borderLeft: '1px solid #e8e8e8',
            backgroundColor: 'white',
            overflowY: 'auto',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 100
          }}>
            {/* 編輯器標題 */}
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #e8e8e8',
              backgroundColor: '#fafafa'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
{t('eformDesigner.editComponent')}: {selectedComponent?.get('tagName')?.toUpperCase() || t('eformDesigner.unknown')}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
{t('eformDesigner.componentId')}: {selectedComponent?.getId() || 'N/A'}
              </p>
            </div>

            {/* 編輯器內容 */}
            <div style={{ padding: '20px' }}>
              {selectedComponent && (
                <ComponentEditor 
                  component={selectedComponent}
                  grapesEditor={grapesEditor}
                  onClose={() => setEditPanelOpen(false)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* 模態框組件 - 保留上傳和AI功能 */}
      <EFormDesignerUpload
        visible={uploadModalVisible}
        uploadType={uploadType}
        onClose={() => setUploadModalVisible(false)}
        onSuccess={handleUploadSuccess}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
      />

      <EFormFieldDisplaySetting
        visible={fieldDisplaySettingVisible}
        onClose={() => setFieldDisplaySettingVisible(false)}
        htmlContent={htmlContent}
        onSave={handleFieldDisplaySettingSave}
        initialSettings={fieldDisplaySettings}
      />
    </div>
  );
};

// 組件編輯器組件
const ComponentEditor = ({ component, grapesEditor, onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [updateKey, setUpdateKey] = useState(0); // 用於強制重新渲染

  // 讀取組件數據的函數
  const readComponentData = useCallback(() => {
    if (!component || !component.get || typeof component.get !== 'function') {
      return null;
    }
    
    try {
      const tagName = component.get('tagName');
      if (!tagName) {
        return null;
      }
      
      const attributes = component.getAttributes();
      
      // 優先檢查是否有 editFormData
      const editFormData = component.get('editFormData');
      if (editFormData) {
        console.log('✅ ComponentEditor 讀取 editFormData:', editFormData);
        return editFormData;
      }
      
      // 如果沒有 editFormData，從屬性初始化
      console.log('⚠️ ComponentEditor 沒有 editFormData，使用屬性初始化');
      return { ...attributes, tagName };
    } catch (error) {
      console.warn('⚠️ ComponentEditor 讀取數據時出錯:', error);
      return null;
    }
  }, [component]);

  // 根據組件類型初始化表單數據
  useEffect(() => {
    console.log('🔍 ComponentEditor useEffect 觸發，組件:', component, 'updateKey:', updateKey);
    
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ ComponentEditor useEffect: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      if (!tagName) {
        console.warn('⚠️ ComponentEditor useEffect: 組件標籤名稱無效');
        return;
      }
      
      const attributes = component.getAttributes();
      
      console.log('🔍 組件標籤:', tagName);
      console.log('🔍 組件屬性:', attributes);
      
      // 讀取 editFormData 的函數
      const readFormData = () => {
        const editFormData = component.get('editFormData');
        if (editFormData) {
          console.log('✅ 使用 editFormData:', editFormData);
          setFormData(editFormData);
          return true;
        }
        return false;
      };
      
      // 優先檢查是否有 editFormData
      if (readFormData()) {
        return;
      }
      
      console.log('⚠️ 沒有 editFormData，使用屬性初始化');
      
      switch (tagName.toLowerCase()) {
        case 'input':
          const attrDataInputType = attributes['data-input-type'];
          const inputType = attrDataInputType === 'currency' ? 'currency' : (attrDataInputType === 'date' ? 'date' : (attributes.type || 'text'));
          if (inputType === 'radio') {
            setFormData({
              type: inputType,
              placeholder: attributes.placeholder || '',
              value: attributes.value || '',
              name: attributes.name || '',
              required: attributes.required || false,
              disabled: attributes.disabled || false,
              options: []
            });
          } else if (inputType === 'checkbox') {
            setFormData({
              type: inputType,
              placeholder: attributes.placeholder || '',
              value: attributes.value || '',
              name: attributes.name || '',
              required: attributes.required || false,
              disabled: attributes.disabled || false,
              checked: attributes.checked || false
            });
          } else {
            // 優先獲取實際顯示的內容
            let inputValue = '';
            try {
              // 優先從 DOM 元素獲取 value 屬性，這是最可靠的方法
              const el = component.getEl ? component.getEl() : null;
              if (el) {
                inputValue = el.value || el.getAttribute('value') || '';
                console.log('✅ 從 DOM 獲取 input value:', inputValue);
              }
              
              // 如果 DOM 方法失敗，嘗試其他方法
              if (!inputValue) {
                inputValue = component.get('content') || attributes.value || '';
                console.log('⚠️ 使用備用方法獲取 input value:', inputValue);
              }
            } catch (error) {
              console.warn('⚠️ 獲取 input value 時出錯:', error);
              inputValue = attributes.value || '';
            }
            
            const inputFormData = {
              type: inputType,
              placeholder: attributes.placeholder || '',
              value: inputValue,
              name: attributes.name || '',
              required: attributes.required || false,
              disabled: attributes.disabled || false,
              readonly: attributes.readonly || false,
              maxlength: attributes.maxlength || '',
              minlength: attributes.minlength || '',
              pattern: attributes.pattern || '',
              title: attributes.title || ''
            };
            
            // 如果是日期類型，添加日期格式信息
            if (inputType === 'date') {
              inputFormData.dateFormat = attributes['data-date-format'] || 'YYYY-MM-DD';
            }
            
            console.log('🔧 input 組件數據初始化:', inputFormData);
            setFormData(inputFormData);
          }
          break;
          
        case 'textarea':
          // 優先從 DOM 元素獲取 textContent，這是最可靠的方法
          let textareaValue = '';
          try {
            const el = component.getEl ? component.getEl() : null;
            if (el) {
              console.log('🔍 textarea DOM 元素:', el);
              console.log('🔍 textarea DOM 元素標籤:', el.tagName);
              console.log('🔍 textarea DOM 元素 innerHTML:', el.innerHTML);
              console.log('🔍 textarea DOM 元素 textContent:', el.textContent);
              console.log('🔍 textarea DOM 元素 innerText:', el.innerText);
              
              // 嘗試多種方法獲取內容
              textareaValue = el.textContent || el.innerText || el.innerHTML || '';
              
              // 如果內容為空，嘗試從組件的其他屬性獲取
              if (!textareaValue) {
                textareaValue = component.get('content') || component.get('innerHTML') || '';
                console.log('⚠️ 從組件屬性獲取 textarea 內容:', textareaValue);
              }
              
              // 如果仍然為空，嘗試從組件的子組件獲取
              if (!textareaValue) {
                try {
                  const components = component.get('components');
                  if (components && components.length > 0) {
                    // textarea 的內容通常存儲在子組件中
                    const firstChild = components[0];
                    if (firstChild && firstChild.get && typeof firstChild.get === 'function') {
                      textareaValue = firstChild.get('content') || '';
                      console.log('⚠️ 從子組件獲取 textarea 內容:', textareaValue);
                    }
                  }
                } catch (childError) {
                  console.warn('⚠️ 獲取子組件內容時出錯:', childError);
                }
              }
              
              console.log('✅ 最終獲取的 textarea 內容:', textareaValue);
            } else {
              console.warn('⚠️ textarea 組件沒有 DOM 元素');
            }
            
            // 如果 DOM 方法失敗，嘗試其他方法
            if (!textareaValue) {
              textareaValue = component.get('content') || component.get('innerHTML') || '';
              console.log('⚠️ 使用備用方法獲取 textarea 內容:', textareaValue);
            }
            
            // 最後的備用方案：檢查子組件
            if (!textareaValue) {
              try {
                const components = component.get('components');
                if (components && components.length > 0) {
                  const firstChild = components[0];
                  if (firstChild && firstChild.get && typeof firstChild.get === 'function') {
                    textareaValue = firstChild.get('content') || '';
                    console.log('⚠️ 最終備用方案：從子組件獲取 textarea 內容:', textareaValue);
                  }
                }
              } catch (finalChildError) {
                console.warn('⚠️ 最終備用方案獲取子組件內容時出錯:', finalChildError);
              }
            }
          } catch (error) {
            console.warn('⚠️ 獲取 textarea 內容時出錯:', error);
            textareaValue = component.get('content') || '';
          }
          
          const textareaData = {
            placeholder: attributes.placeholder || '',
            value: textareaValue,
            defaultValue: textareaValue, // 確保 defaultValue 和 value 一致
            name: attributes.name || '',
            required: attributes.required || false,
            disabled: attributes.disabled || false,
            readonly: attributes.readonly || false,
            rows: attributes.rows || '4',
            cols: attributes.cols || '50',
            maxlength: attributes.maxlength || '',
            minlength: attributes.minlength || ''
          };
          console.log('🔧 textarea 組件數據初始化:', textareaData);
          setFormData(textareaData);
          break;
          
        case 'select':
          console.log('🔍 處理 Select 組件...');
          setFormData({
            name: attributes.name || '',
            required: attributes.required || false,
            disabled: attributes.disabled || false,
            multiple: attributes.multiple || false,
            size: attributes.size || '1',
            options: []
          });
          
          // 優先使用 editFormData 中的選項（如果有的話）
          const editFormData = component.get('editFormData');
          console.log('🔍 editFormData:', editFormData);
          
          if (editFormData && editFormData.options && editFormData.options.length > 0) {
            console.log('✅ 使用 editFormData 中的選項:', editFormData.options);
            setFormData(prev => ({ ...prev, options: editFormData.options }));
          } else {
            // 如果沒有 editFormData，則手動解析選項
            console.log('⚠️ 沒有 editFormData，手動解析選項');
            const options = component.get('components');
            console.log('🔍 組件子元素:', options);
            
            if (options && options.length > 0) {
              const parsedOptions = options.map(option => {
                let text = '';
                const value = option.getAttributes().value || '';
                const selected = option.getAttributes().selected || false;
                
                console.log('🔍 解析 option:', { value, selected });
                
                // 優先從 DOM 元素獲取文字內容
                const el = option.getEl ? option.getEl() : null;
                if (el) {
                  text = el.textContent || el.innerText || '';
                  console.log('✅ 從 DOM 獲取文字:', text);
                }
                
                // 如果 DOM 方法失敗，嘗試其他方法
                if (!text) {
                  text = option.get('content') || option.get('innerHTML') || option.get('text') || '';
                  console.log('⚠️ 使用備用方法獲取文字:', text);
                }
                
                // 如果文字為空但 value 有值，使用 value 作為文字
                if (!text && value) {
                  text = value;
                  console.log('🔄 使用 value 作為文字:', text);
                }
                
                const result = {
                  value: value,
                  text: text.trim(),
                  selected: selected
                };
                
                console.log('🔍 option 解析結果:', result);
                return result;
              });
              console.log('✅ 手動解析的選項:', parsedOptions);
              setFormData(prev => ({ ...prev, options: parsedOptions }));
            }
          }
          break;
          
        case 'button':
          setFormData({
            type: attributes.type || 'button',
            value: component.get('content') || '',
            name: attributes.name || '',
            disabled: attributes.disabled || false,
            form: attributes.form || '',
            formaction: attributes.formaction || '',
            formmethod: attributes.formmethod || 'get',
            formnovalidate: attributes.formnovalidate || false
          });
          break;
          
        case 'label':
          setFormData({
            for: attributes.for || '',
            value: component.get('content') || '',
            required: attributes.required || false
          });
          break;
          
        case 'td':
        case 'th':
          // 處理 table cell，允許編輯文字內容
          let cellContent = '';
          try {
            const el = component.getEl();
            if (el) {
              cellContent = el.textContent || el.innerText || el.innerHTML || '';
            }
            if (!cellContent) {
              cellContent = component.get('content') || component.get('innerHTML') || '';
            }
          } catch (error) {
            console.warn('⚠️ 獲取 table cell 內容時出錯:', error);
            cellContent = component.get('content') || '';
          }
          
          setFormData({
            tagName: tagName,
            content: cellContent,
            ...attributes
          });
          break;

        case 'div':
          if (attributes['data-gjs-type'] !== 'text') {
            setFormData({ tagName: tagName, content: component.get('content') || '', ...attributes });
            break;
          }
          // 文字元素 (data-gjs-type="text")：提供文字格式 (對齊、字體、字級等)
          (() => {
            let textContent = '';
            try {
              const el = component.getEl();
              if (el) {
                textContent = el.textContent || el.innerText || el.innerHTML || '';
              }
              if (textContent === undefined || textContent === '') {
                textContent = component.get('content') || component.get('innerHTML') || '';
              }
            } catch (e) {
              textContent = component.get('content') || '';
            }
            const styleStr = attributes.style || component.getStyle?.()?.get('') || '';
            const parseStyle = (str) => {
              if (!str || typeof str !== 'string') return {};
              const obj = {};
              str.split(';').forEach((part) => {
                const idx = part.indexOf(':');
                if (idx === -1) return;
                const key = part.slice(0, idx).trim().toLowerCase();
                const val = part.slice(idx + 1).trim();
                if (key && val) obj[key] = val;
              });
              return obj;
            };
            const parsed = parseStyle(styleStr);
            let fontStyleType = 'normal';
            const fw = (parsed['font-weight'] || 'normal').toLowerCase();
            const fs = (parsed['font-style'] || 'normal').toLowerCase();
            const td = (parsed['text-decoration'] || 'none').toLowerCase();
            if (td === 'underline') fontStyleType = 'underline';
            else if (fw === 'bold' && fs === 'italic') fontStyleType = 'bold-italic';
            else if (fw === 'bold') fontStyleType = 'bold';
            else if (fs === 'italic') fontStyleType = 'italic';
            setFormData({
              tagName: tagName,
              content: typeof textContent === 'string' ? textContent : (textContent || ''),
              textAlign: parsed['text-align'] || 'left',
              fontFamily: parsed['font-family'] || '',
              fontSize: parsed['font-size'] || '14px',
              fontStyleType: fontStyleType,
              ...attributes
            });
          })();
          break;

        case 'p':
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          // 段落與標題：提供文字格式 (對齊、字體、字級等)
          (() => {
            let textContent = '';
            try {
              const el = component.getEl();
              if (el) {
                textContent = el.textContent || el.innerText || el.innerHTML || '';
              }
              if (textContent === undefined || textContent === '') {
                textContent = component.get('content') || component.get('innerHTML') || '';
              }
            } catch (e) {
              textContent = component.get('content') || '';
            }
            const styleStr = attributes.style || component.getStyle?.()?.get('') || '';
            const parseStyle = (str) => {
              if (!str || typeof str !== 'string') return {};
              const obj = {};
              str.split(';').forEach((part) => {
                const idx = part.indexOf(':');
                if (idx === -1) return;
                const key = part.slice(0, idx).trim().toLowerCase();
                const val = part.slice(idx + 1).trim();
                if (key && val) obj[key] = val;
              });
              return obj;
            };
            const parsed = parseStyle(styleStr);
            let fontStyleType = 'normal';
            const fw = (parsed['font-weight'] || 'normal').toLowerCase();
            const fs = (parsed['font-style'] || 'normal').toLowerCase();
            const td = (parsed['text-decoration'] || 'none').toLowerCase();
            if (td === 'underline') fontStyleType = 'underline';
            else if (fw === 'bold' && fs === 'italic') fontStyleType = 'bold-italic';
            else if (fw === 'bold') fontStyleType = 'bold';
            else if (fs === 'italic') fontStyleType = 'italic';
            setFormData({
              tagName: tagName,
              content: typeof textContent === 'string' ? textContent : (textContent || ''),
              textAlign: parsed['text-align'] || 'left',
              fontFamily: parsed['font-family'] || '',
              fontSize: parsed['font-size'] || '14px',
              fontStyleType: fontStyleType,
              ...attributes
            });
          })();
          break;
          
        default:
          setFormData({
            tagName: tagName,
            content: component.get('content') || '',
            ...attributes
          });
          break;
      }
    } catch (error) {
      console.error('❌ ComponentEditor useEffect 發生錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  }, [component, updateKey]);

  // 監聽組件的 editFormData 變化（當拖拽調整大小時）
  useEffect(() => {
    if (!component || !grapesEditor) return;
    
    const componentId = component.getId ? component.getId() : null;
    if (!componentId) return;
    
    console.log('🎧 ComponentEditor 開始監聽組件更新事件，組件ID:', componentId);
    
    // 監聽組件更新事件
    const handleComponentUpdate = (updatedComponent) => {
      if (!updatedComponent) return;
      
      try {
        const updatedId = updatedComponent.getId ? updatedComponent.getId() : null;
        if (updatedId === componentId) {
          console.log('🔄 ComponentEditor 檢測到選中組件更新，刷新表單數據');
          
          // 重新讀取 editFormData
          const editFormData = updatedComponent.get('editFormData');
          if (editFormData) {
            console.log('✅ ComponentEditor 更新 formData:', editFormData);
            setFormData(editFormData);
            // 觸發重新渲染
            setUpdateKey(prev => prev + 1);
          }
        }
      } catch (error) {
        console.warn('⚠️ ComponentEditor 處理組件更新時出錯:', error);
      }
    };
    
    // 綁定事件監聽器
    grapesEditor.on('component:update', handleComponentUpdate);
    
    // 清理函數
    return () => {
      console.log('🧹 ComponentEditor 清理組件更新監聽器');
      grapesEditor.off('component:update', handleComponentUpdate);
    };
  }, [component, grapesEditor]);

  // 處理表單變化
  const handleFormChange = (field, value) => {
    const componentId = component?.getId ? component.getId() : 'unknown';
    const componentAttrId = component?.getAttributes ? (component.getAttributes().id || '') : '';
    console.log(`📝 [handleFormChange] 表單字段變更:`, {
      field,
      value: value && value.length > 100 ? value.substring(0, 100) + '...' : value,
      componentId,
      componentAttrId,
      tagName: component?.get('tagName')
    });
    
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      console.log('📝 [handleFormChange] 更新後的表單數據:', newData);
      
      // 同時更新組件的 editFormData 以保持一致性
      if (component && component.get && typeof component.get === 'function') {
        try {
          const tagName = component.get('tagName')?.toLowerCase();
          
          // 如果是 img 組件且上傳了新圖片，清除固定的 width 和 height
          if (tagName === 'img' && field === 'src' && value && 
              !value.includes('via.placeholder.com') && 
              !value.includes('/assets/no-image.jpg') &&
              !value.startsWith('data:image')) {
            console.log(`🖼️ [handleFormChange] 檢測到新圖片上傳 (組件ID: ${componentId}, 屬性ID: ${componentAttrId})，清除固定尺寸，使用原始尺寸`);
            
            // 清除表單數據中的 width 和 height
            newData.width = '';
            newData.height = '';
            
            // 清除組件屬性中的 width 和 height
            const currentAttrs = component.getAttributes() || {};
            const updatedAttrs = { ...currentAttrs };
            delete updatedAttrs.width;
            delete updatedAttrs.height;
            
            // 清除樣式中的 width 和 height，保留其他樣式
            let styleStr = currentAttrs.style || '';
            if (styleStr) {
              const styleObj = {};
              styleStr.split(';').forEach(rule => {
                const [key, val] = rule.split(':').map(s => s.trim());
                if (key && val && key !== 'width' && key !== 'height') {
                  styleObj[key] = val;
                }
              });
              // 設置響應式樣式，但不清除其他樣式
              styleObj['max-width'] = '100%';
              styleObj['height'] = 'auto';
              styleStr = Object.entries(styleObj)
                .map(([key, val]) => `${key}: ${val}`)
                .join('; ');
            } else {
              styleStr = 'max-width: 100%; height: auto';
            }
            
            // 記錄更新前的狀態
            const beforeAttrs = component.getAttributes();
            const beforeEditFormData = component.get('editFormData') || {};
            const beforeImgEl = component.getEl();
            const beforeDomSrc = beforeImgEl ? (beforeImgEl.getAttribute('src') || beforeImgEl.src) : null;
            
            console.log(`🔍 [handleFormChange] 更新前狀態 (組件ID: ${componentId}):`, {
              attrsSrc: beforeAttrs.src,
              editFormDataSrc: beforeEditFormData.src,
              domSrc: beforeDomSrc ? (beforeDomSrc.length > 100 ? beforeDomSrc.substring(0, 100) + '...' : beforeDomSrc) : null,
              newSrc: value
            });
            
            // 更新組件屬性 - 關鍵：同時更新 src 屬性
            component.setAttributes({
              ...updatedAttrs,
              src: value, // 🔧 添加：更新 src 屬性
              style: styleStr
            }, { silent: false }); // 確保觸發 change 事件
            
            // 驗證組件屬性更新
            const afterAttrs = component.getAttributes();
            console.log(`✅ [handleFormChange] 組件屬性已更新 (組件ID: ${componentId}):`, {
              before: beforeAttrs.src,
              after: afterAttrs.src,
              expected: value,
              success: afterAttrs.src === value
            });
            
            // 更新 DOM 元素 - 關鍵：同時更新 src 屬性
            const imgEl = component.getEl();
            if (imgEl) {
              // 🔧 更新 src 屬性（最重要）
              const absoluteSrc = value.startsWith('/')
                ? `${window.location.origin}${value}`
                : value;
              imgEl.src = absoluteSrc;
              imgEl.setAttribute('src', value); // 同時設置屬性，確保 HTML 中也有
              
              // 清除 width 和 height 屬性
              imgEl.removeAttribute('width');
              imgEl.removeAttribute('height');
              imgEl.style.width = '';
              imgEl.style.height = '';
              if (styleStr) {
                imgEl.style.cssText = styleStr;
              }
              
              // 驗證 DOM 更新
              const afterDomSrc = imgEl.getAttribute('src') || imgEl.src;
              console.log(`✅ [handleFormChange] DOM 元素已更新 (組件ID: ${componentId}):`, {
                before: beforeDomSrc ? (beforeDomSrc.length > 100 ? beforeDomSrc.substring(0, 100) + '...' : beforeDomSrc) : null,
                after: afterDomSrc ? (afterDomSrc.length > 100 ? afterDomSrc.substring(0, 100) + '...' : afterDomSrc) : null,
                expected: value,
                success: afterDomSrc === value || afterDomSrc === absoluteSrc
              });
            }
            
            // 更新 editFormData
            const currentEditFormData = component.get('editFormData') || {};
            currentEditFormData.src = value;
            currentEditFormData.width = '';
            currentEditFormData.height = '';
            currentEditFormData.style = styleStr;
            component.set('editFormData', currentEditFormData);
            
            // 驗證 editFormData 更新
            const afterEditFormData = component.get('editFormData') || {};
            console.log(`✅ [handleFormChange] editFormData 已更新 (組件ID: ${componentId}):`, {
              before: beforeEditFormData.src,
              after: afterEditFormData.src,
              expected: value,
              success: afterEditFormData.src === value
            });
            
            // 🔧 觸發組件更新事件，確保 GrapesJS 正確同步
            try {
              if (component.trigger) {
                component.trigger('change:attributes');
                component.trigger('change');
              }
              
              // 🔧 觸發編輯器級別的更新事件（使用 setTimeout 避免在更新過程中觸發）
              if (grapesEditor && grapesEditor.trigger && typeof grapesEditor.trigger === 'function') {
                // 使用 setTimeout 延遲觸發，確保所有屬性更新完成
                setTimeout(() => {
                  try {
                    if (grapesEditor && !grapesEditor.isDestroyed) {
                      grapesEditor.trigger('component:update', component);
                    }
                  } catch (triggerError) {
                    console.warn('⚠️ 觸發編輯器更新事件時出錯:', triggerError);
                  }
                }, 10);
              }
            } catch (error) {
              console.warn('⚠️ 觸發組件更新事件時出錯:', error);
            }
            
            console.log('✅ editFormData 已同步（已清除尺寸，src 已更新）:', currentEditFormData);
          } else {
            // 正常更新 editFormData
            const currentEditFormData = component.get('editFormData') || {};
            currentEditFormData[field] = value;
            component.set('editFormData', currentEditFormData);
            
            // 🔧 如果是 img 組件的其他字段（如 alt），也需要同步到組件屬性
            if (tagName === 'img' && field !== 'src') {
              component.setAttributes({ [field]: value }, { silent: false });
              const imgEl = component.getEl();
              if (imgEl && field === 'alt') {
                imgEl.setAttribute('alt', value);
                imgEl.alt = value;
              }
            }

            // 🔧 文字元素 (div data-gjs-type=text / p / h1-h6)：即時套用樣式與內容到組件
            const isTextBlock = (tagName === 'div' && (component.getAttributes() || {})['data-gjs-type'] === 'text') || ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName);
            if (isTextBlock && ['content', 'textAlign', 'fontFamily', 'fontSize', 'fontStyleType'].includes(field)) {
              const newData = { ...formData, [field]: value };
              const styleParts = [];
              if (newData.textAlign) styleParts.push(`text-align: ${newData.textAlign}`);
              if (newData.fontFamily) styleParts.push(`font-family: ${newData.fontFamily}`);
              if (newData.fontSize) styleParts.push(`font-size: ${newData.fontSize}`);
              const ft = newData.fontStyleType || 'normal';
              if (ft === 'bold') styleParts.push('font-weight: bold');
              else if (ft === 'italic') styleParts.push('font-style: italic');
              else if (ft === 'bold-italic') { styleParts.push('font-weight: bold'); styleParts.push('font-style: italic'); }
              else if (ft === 'underline') styleParts.push('text-decoration: underline');
              else { styleParts.push('font-weight: normal'); styleParts.push('font-style: normal'); styleParts.push('text-decoration: none'); }
              const styleStr = styleParts.join('; ');
              component.setAttributes({ style: styleStr }, { silent: false });
              const el = component.getEl();
              if (el) el.style.cssText = styleStr;
              if (newData.content !== undefined) {
                component.components((typeof newData.content === 'string' ? newData.content : '') || '');
                component.set('content', (typeof newData.content === 'string' ? newData.content : '') || '', { silent: false });
                if (el) {
                  el.textContent = (typeof newData.content === 'string' ? newData.content : '') || '';
                }
              }
              if (grapesEditor && grapesEditor.trigger) {
                grapesEditor.trigger('component:update', component);
              }
            }
            
            console.log('✅ editFormData 已同步:', currentEditFormData);
          }
          
          // 如果是 table cell (td/th) 的內容變更，立即同步到組件和 DOM
          if ((tagName === 'td' || tagName === 'th') && field === 'content') {
            console.log('📝 table cell 內容變更，立即同步:', value);
            
            // 確保 editFormData.content 也被更新（用於保存時讀取）
            const currentEditFormData = component.get('editFormData') || {};
            currentEditFormData.content = value;
            component.set('editFormData', currentEditFormData);
            console.log('✅ table cell editFormData.content 已更新:', value);
            
            // 關鍵：根據 GrapesJS 官方文檔，應該使用 component.components(newContent) 來更新組件內容
            // 這個方法會替換組件的子組件，並且 getHtml() 會正確反映這個變化
            // 參考：https://grapesjs.com/docs/api/component.html
            
            try {
              // 方法1: 使用官方推薦的 components() 方法更新內容
              // components(newContent) 會替換所有子組件，這是 getHtml() 使用的數據源
              component.components(value || '');
              console.log(`✅ 使用 component.components() 更新 table cell 內容: "${value}"`);
              
              // 同時更新屬性以確保一致性（某些操作可能依賴這些屬性）
              component.set('content', value || '', { silent: false });
              component.set('innerHTML', value || '', { silent: false });
              component.set('textContent', value || '', { silent: false });
              
              // 更新 DOM 以確保視覺正確
              const cellEl = component.getEl();
              if (cellEl) {
                // 清除所有子節點
                while (cellEl.firstChild) {
                  cellEl.removeChild(cellEl.firstChild);
                }
                // 添加新文本內容
                if (value) {
                  cellEl.appendChild(document.createTextNode(value));
                }
              }
              
              // 觸發組件更新事件，確保 GrapesJS 正確同步
              if (component.trigger) {
                component.trigger('change:content', { value: value });
                component.trigger('change');
              }
              
              // 觸發編輯器級別的更新事件，確保 getHtml() 能獲取最新內容
              if (grapesEditor && grapesEditor.trigger) {
                grapesEditor.trigger('component:update', component);
                grapesEditor.trigger('update');
                grapesEditor.trigger('storage:store');
              }
              
              console.log('✅ table cell 組件內容已更新:', value);
            } catch (error) {
              console.error('❌ 更新 table cell 內容時出錯:', error);
              // 降級方案：直接設置屬性
              component.set('content', value || '', { silent: false });
              component.set('innerHTML', value || '', { silent: false });
              component.set('textContent', value || '', { silent: false });
            }
            
            // 舊實現已移除，現在使用 GrapesJS 官方推薦的 component.components(newContent) 方法
            return; // 直接返回，不執行後續的舊代碼
            // 步驟1: 更新組件的子組件集合（這是 GrapesJS getHtml() 使用的數據源，最關鍵）
            try {
              // 獲取組件的子組件集合和子組件數組
              const componentsCollection = component.components;
              const components = componentsCollection && typeof componentsCollection === 'function' ? componentsCollection() : null;
              
              // 檢查是否有非文本子組件（如 img），需要保留它們
              let nonTextChildren = [];
              if (components && Array.isArray(components) && components.length > 0) {
                nonTextChildren = components.filter(child => {
                  if (!child || !child.get) return false;
                  const childTag = child.get('tagName');
                  // 保留非文本節點（如 img, br 等）
                  return childTag && childTag.toLowerCase() !== 'text' && childTag.toLowerCase() !== '#text';
                });
                if (nonTextChildren.length > 0) {
                  console.log(`🔍 table cell 有 ${nonTextChildren.length} 個非文本子組件，將保留它們`);
                }
              }
              
              // 關鍵：直接操作組件的子組件集合（這是 GrapesJS getHtml() 使用的數據源）
              if (componentsCollection) {
                // 嘗試使用 setComponents 方法（如果可用）
                const hasSetComponents = typeof componentsCollection.setComponents === 'function';
                const hasReset = typeof componentsCollection.reset === 'function';
                
                if (nonTextChildren.length > 0) {
                  // 有非文本子組件，需要保留它們並更新文本內容
                  // 構建新內容：非文本子組件 HTML + 新文本
                  const childHtml = nonTextChildren.map(child => {
                    const childEl = child.getEl ? child.getEl() : null;
                    return childEl ? childEl.outerHTML : '';
                  }).join('');
                  const newContent = childHtml + (value || '');
                  
                  // 設置 content 屬性（GrapesJS 會解析 HTML 並更新子組件）
                  component.set('content', newContent, { silent: false });
                  
                  console.log(`🔍 更新 table cell 內容（保留 ${nonTextChildren.length} 個非文本子組件）: "${value}"`);
                } else {
                  // 沒有非文本子組件，直接重置為純文本
                  // 關鍵：必須正確更新子組件集合，讓 getHtml() 能獲取最新內容
                  try {
                    // 方法1: 先更新 DOM，然後使用 GrapesJS 的同步機制
                    const cellEl = component.getEl();
                    if (cellEl) {
                      // 清除所有子節點
                      while (cellEl.firstChild) {
                        cellEl.removeChild(cellEl.firstChild);
                      }
                      // 添加新文本內容
                      if (value) {
                        cellEl.appendChild(document.createTextNode(value));
                      }
                    }
                    
                    // 方法2: 使用 reset 方法設置新的文本內容
                    // 注意：reset() 接受字符串時，GrapesJS 會將其解析為文本內容
                    // 但為了確保正確，我們先清除所有子組件
                    if (components && components.length > 0) {
                      // 先移除所有現有子組件
                      const componentsToRemove = [...components];
                      componentsToRemove.forEach(child => {
                        if (child && child.remove) {
                          child.remove({ silent: true });
                        }
                      });
                    }
                    
                    // 然後使用 reset 設置新內容（空字符串時重置為空）
                    if (hasReset) {
                      // 關鍵：reset() 會創建新的組件結構
                      // 如果 value 是純文本，GrapesJS 會創建文本節點組件
                      componentsCollection.reset(value || '');
                      console.log(`✅ 使用 reset 重置 table cell 子組件為純文本: "${value}"`);
                    } else if (hasSetComponents) {
                      // 使用 setComponents 設置新的文本內容
                      componentsCollection.setComponents(value || '');
                      console.log(`✅ 使用 setComponents 設置 table cell 子組件為純文本: "${value}"`);
                    }
                    
                    // 方法3: 確保組件屬性也更新（這很重要，因為某些操作可能依賴這些屬性）
                    component.set('content', value || '', { silent: false });
                    component.set('innerHTML', value || '', { silent: false });
                    component.set('textContent', value || '', { silent: false });
                    
                    console.log('✅ table cell 組件屬性已更新');
                  } catch (updateError) {
                    console.warn('⚠️ 更新子組件失敗，使用降級方案:', updateError);
                    // 降級：直接設置 content 和更新 DOM
                    component.set('content', value || '', { silent: false });
                    component.set('innerHTML', value || '', { silent: false });
                    component.set('textContent', value || '', { silent: false });
                    
                    // 確保 DOM 也更新
                    const cellEl = component.getEl();
                    if (cellEl) {
                      while (cellEl.firstChild) {
                        cellEl.removeChild(cellEl.firstChild);
                      }
                      if (value) {
                        cellEl.appendChild(document.createTextNode(value));
                      }
                    }
                  }
                }
              } else {
                // 降級方案：如果無法訪問 components 集合，直接設置 content 屬性
                console.warn('⚠️ 無法訪問 components 集合，使用降級方案');
                component.set('content', value || '', { silent: false });
                component.set('innerHTML', value || '', { silent: false });
                component.set('textContent', value || '', { silent: false });
              }
              
              // 步驟2: 更新 DOM，確保可視化正確
              try {
                const cellEl = component.getEl();
                if (cellEl) {
                  // 清除所有子節點
                  while (cellEl.firstChild) {
                    cellEl.removeChild(cellEl.firstChild);
                  }
                  // 添加新文本內容
                  if (value) {
                    cellEl.appendChild(document.createTextNode(value));
                  }
                  console.log('✅ DOM 已更新:', value);
                }
              } catch (domError) {
                console.warn('⚠️ 更新 DOM 時出錯:', domError);
              }
              
              // 步驟3: 強制讓 GrapesJS 重新解析組件（從 DOM 同步回組件結構）
              // 這是最關鍵的步驟，因為 getHtml() 依賴組件的子組件結構
              try {
                const cellEl = component.getEl();
                if (cellEl && grapesEditor && grapesEditor.DomComponents) {
                  // 使用 GrapesJS 的組件模型來重新解析組件
                  // 這會從 DOM 讀取內容並更新組件的子組件結構
                  const domComponents = grapesEditor.DomComponents;
                  
                  // 獲取組件的視圖
                  const componentView = component.getView();
                  if (componentView && componentView.updateContent) {
                    // 強制更新組件視圖
                    componentView.updateContent();
                  }
                  
                  // 或者直接觸發組件的 change 事件，讓 GrapesJS 重新同步
                  if (component.trigger) {
                    component.trigger('change:content', { value: value, component: component });
                    component.trigger('change');
                  }
                }
              } catch (syncError) {
                console.warn('⚠️ 強制同步組件結構時出錯:', syncError);
              }
              
              // 步驟4: 觸發編輯器級別的更新事件，確保 getHtml() 能獲取最新內容
              if (grapesEditor && grapesEditor.trigger) {
                // 觸發組件更新事件
                grapesEditor.trigger('component:update', component);
                // 觸發編輯器內容變更事件，這會讓 "view code" 功能刷新
                grapesEditor.trigger('update');
                // 觸發存儲變更事件，標記編輯器為 "dirty"
                grapesEditor.trigger('storage:store');
              }
              
              // 步驟5: 使用 requestAnimationFrame 確保所有更新在下一個渲染週期完成
              // 這確保 DOM 更新和組件更新都已完成
              requestAnimationFrame(() => {
                // 確保 DOM 和組件結構一致
                try {
                  const cellEl = component.getEl();
                  const finalComponentsCollection = component.components;
                  const componentsAfterUpdate = finalComponentsCollection && typeof finalComponentsCollection === 'function' ? finalComponentsCollection() : null;
                  const domText = cellEl ? (cellEl.textContent || cellEl.innerText || '').trim() : '';
                  const expectedText = (value || '').trim();
                  
                  // 檢查組件的子組件結構是否正確
                  const hasTextComponent = componentsAfterUpdate && Array.isArray(componentsAfterUpdate) && componentsAfterUpdate.length > 0;
                  const componentText = hasTextComponent ? 
                    componentsAfterUpdate.map(child => {
                      if (!child || !child.get) return '';
                      const childTag = child.get('tagName');
                      if (childTag && (childTag.toLowerCase() === 'text' || childTag.toLowerCase() === '#text')) {
                        return child.get('content') || child.get('text') || '';
                      }
                      const childEl = child.getEl ? child.getEl() : null;
                      return childEl ? (childEl.textContent || childEl.innerText || '') : '';
                    }).join('').trim() : '';
                  
                  // 如果組件結構或 DOM 與預期不一致，強制重新設置
                  if ((domText !== expectedText || componentText !== expectedText) && expectedText) {
                    console.warn('⚠️ DOM 或組件結構與預期不一致，強制重新同步:', { 
                      domText, 
                      componentText, 
                      expectedText,
                      hasComponents: hasTextComponent
                    });
                    
                    // 再次清除並重新設置 DOM
                    if (cellEl) {
                      while (cellEl.firstChild) {
                        cellEl.removeChild(cellEl.firstChild);
                      }
                      if (value) {
                        cellEl.appendChild(document.createTextNode(value));
                      }
                    }
                    
                    // 再次使用 reset 確保子組件結構正確
                    if (finalComponentsCollection && typeof finalComponentsCollection.reset === 'function') {
                      finalComponentsCollection.reset(value || '');
                      component.set('content', value || '', { silent: false });
                      component.set('innerHTML', value || '', { silent: false });
                      component.set('textContent', value || '', { silent: false });
                    }
                  }
                  
                  // 再次觸發更新事件，確保 GrapesJS 重新構建 HTML
                  if (grapesEditor && grapesEditor.trigger) {
                    grapesEditor.trigger('component:update', component);
                    grapesEditor.trigger('update');
                    // 特別觸發 change:components 事件，通知子組件已變更
                    if (component.trigger) {
                      component.trigger('change:components');
                    }
                  }
                } catch (e) {
                  console.warn('⚠️ requestAnimationFrame 同步時出錯:', e);
                }
              });
              
              console.log('✅ table cell 組件內容已更新:', value);
              
              // 強制驗證：確保 getHtml() 能立即獲取最新內容
              // 這是一個額外的檢查，確保更新成功
              setTimeout(() => {
                try {
                  if (grapesEditor && grapesEditor.getHtml) {
                    const testHtml = grapesEditor.getHtml();
                    const cellEl = component.getEl();
                    if (cellEl && cellEl.id) {
                      // 使用 DOMParser 解析 HTML，檢查這個 cell 的內容
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(testHtml, 'text/html');
                      const cellInHtml = doc.getElementById(cellEl.id);
                      if (cellInHtml) {
                        const htmlText = (cellInHtml.textContent || cellInHtml.innerText || '').trim();
                        const expectedText = (value || '').trim();
                        if (htmlText !== expectedText) {
                          console.error(`❌ getHtml() 仍未反映更新! ID: ${cellEl.id}, 預期: "${expectedText}", 實際: "${htmlText}"`);
                          // 最後一次嘗試：強制使用 reset
                          const finalComponentsCollection = component.components;
                          if (finalComponentsCollection && typeof finalComponentsCollection.reset === 'function') {
                            finalComponentsCollection.reset(value || '');
                            component.set('content', value || '', { silent: false });
                            grapesEditor.trigger('component:update', component);
                            grapesEditor.trigger('update');
                          }
                        } else {
                          console.log(`✅ getHtml() 已正確反映更新! ID: ${cellEl.id}, 內容: "${htmlText}"`);
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.warn('⚠️ 最終驗證時出錯:', e);
                }
              }, 100);
              
              // 驗證更新是否成功（使用多次驗證，確保 GrapesJS 有時間處理）
              const verifyUpdate = (attempt = 1, maxAttempts = 3) => {
                setTimeout(() => {
                  const updatedContent = component.get('content');
                  const cellEl = component.getEl();
                  const domContent = cellEl ? (cellEl.textContent || cellEl.innerText || '') : '';
                  
                  // 測試 getHtml() 是否能獲取最新內容
                  let htmlContent = '';
                  try {
                    if (grapesEditor && grapesEditor.getHtml) {
                      const fullHtml = grapesEditor.getHtml();
                      // 嘗試從 HTML 中提取這個 cell 的內容
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(fullHtml, 'text/html');
                      const cellInHtml = doc.getElementById(cellEl ? cellEl.id : '');
                      if (cellInHtml) {
                        htmlContent = cellInHtml.textContent || cellInHtml.innerText || '';
                      }
                    }
                  } catch (e) {
                    console.warn('⚠️ 測試 getHtml() 時出錯:', e);
                  }
                  
                  // 改進的驗證邏輯：考慮到 content 可能包含 HTML 標籤
                  const componentContentText = updatedContent ? updatedContent.replace(/<[^>]*>/g, '').trim() : '';
                  const htmlContentText = htmlContent ? htmlContent.trim() : '';
                  const expectedText = value ? value.trim() : '';
                  
                  const componentMatch = componentContentText === expectedText || componentContentText.includes(expectedText);
                  const domMatch = domContent.trim() === expectedText || domContent.trim().includes(expectedText);
                  const htmlMatch = htmlContentText === expectedText || htmlContentText.includes(expectedText);
                  
                  console.log(`🔍 驗證 table cell 更新 (嘗試 ${attempt}/${maxAttempts}):`, {
                    componentContent: updatedContent,
                    componentContentText: componentContentText,
                    domContent: domContent,
                    htmlContent: htmlContent,
                    htmlContentText: htmlContentText,
                    expected: value,
                    expectedText: expectedText,
                    componentMatch: componentMatch,
                    domMatch: domMatch,
                    htmlMatch: htmlMatch
                  });
                  
                  // 如果 htmlContent 不匹配，說明 getHtml() 沒有反映更新
                  // 這是關鍵問題：需要強制更新組件的子組件結構
                  if (!htmlMatch && expectedText && attempt < maxAttempts) {
                    console.warn(`⚠️ getHtml() 未反映更新 (嘗試 ${attempt}/${maxAttempts})，強制更新組件子組件結構`);
                    
                    // 再次嘗試使用 reset 方法
                    const componentsCollection = component.components;
                    if (componentsCollection && typeof componentsCollection.reset === 'function') {
                      try {
                        // 檢查是否有非文本子組件需要保留
                        const currentComponents = componentsCollection();
                        const nonTextChildren = currentComponents && Array.isArray(currentComponents) 
                          ? currentComponents.filter(child => {
                              if (!child || !child.get) return false;
                              const childTag = child.get('tagName');
                              return childTag && childTag.toLowerCase() !== 'text' && childTag.toLowerCase() !== '#text';
                            })
                          : [];
                        
                        if (nonTextChildren.length > 0) {
                          // 有非文本子組件，構建包含它們的 HTML
                          const childHtml = nonTextChildren.map(child => {
                            const childEl = child.getEl ? child.getEl() : null;
                            return childEl ? childEl.outerHTML : '';
                          }).join('');
                          component.set('content', childHtml + expectedText, { silent: false });
                        } else {
                          // 沒有非文本子組件，直接重置
                          componentsCollection.reset(expectedText);
                          component.set('content', expectedText, { silent: false });
                        }
                        
                        // 觸發更新事件
                        if (component.trigger) {
                          component.trigger('change:content');
                        }
                        if (grapesEditor && grapesEditor.trigger) {
                          grapesEditor.trigger('component:update', component);
                          grapesEditor.trigger('update');
                        }
                        
                        // 如果編輯器有 refresh 方法，調用它
                        if (grapesEditor && grapesEditor.refresh && typeof grapesEditor.refresh === 'function') {
                          grapesEditor.refresh();
                        }
                        
                        // 繼續驗證
                        verifyUpdate(attempt + 1, maxAttempts);
                      } catch (e) {
                        console.error('❌ 強制更新失敗:', e);
                        if (attempt < maxAttempts) {
                          verifyUpdate(attempt + 1, maxAttempts);
                        }
                      }
                    } else {
                      // 無法使用 reset，繼續驗證
                      if (attempt < maxAttempts) {
                        verifyUpdate(attempt + 1, maxAttempts);
                      }
                    }
                  } else if (!htmlMatch && expectedText && attempt >= maxAttempts) {
                    console.error('❌ getHtml() 在多次嘗試後仍無法反映更新');
                  } else if (htmlMatch) {
                    console.log('✅ getHtml() 已正確反映更新');
                  }
                  
                  // 如果組件內容不匹配，強制更新
                  if (!componentMatch && expectedText) {
                    console.warn('⚠️ 組件 content 屬性未正確更新，強制設置');
                    component.set('content', expectedText, { silent: false });
                  }
                  
                  // 如果 DOM 內容不匹配，但組件內容正確，說明 GrapesJS 還沒更新 DOM
                  // 這種情況下不需要手動更新 DOM，讓 GrapesJS 自動處理
                  if (!domMatch && componentMatch) {
                    console.log('ℹ️ DOM 尚未更新，但組件內容已正確，等待 GrapesJS 自動更新');
                  }
                }, attempt === 1 ? 100 : 200); // 第一次驗證稍快，後續驗證間隔更長
              };
              
              // 開始驗證
              verifyUpdate(1, 3);
              
            } catch (error) {
              console.error('❌ 更新 table cell 內容時出錯:', error);
              // 降級方案：直接更新組件屬性（不直接操作 DOM）
              try {
                component.set('content', value, { silent: false });
              } catch (e) {
                console.error('❌ 降級方案也失敗:', e);
              }
            }
            
            console.log('✅ table cell 內容已立即同步到組件、DOM 和 editFormData');
          }
          
          // 如果是 textarea 的內容變更，立即同步到組件的 content 屬性
          if (tagName === 'textarea') {
            if (field === 'defaultValue' || field === 'value') {
              const contentToSync = value || '';
              console.log('🔄 立即同步 textarea 內容到組件:', contentToSync);
              
              // 更新組件的 content 屬性
              component.set('content', contentToSync);
              
              // 同時更新 innerHTML 和 textContent 屬性
              try {
                component.set('innerHTML', contentToSync);
                component.set('textContent', contentToSync);
                console.log('✅ textarea 組件內容屬性已同步');
              } catch (error) {
                console.warn('⚠️ 同步組件內容屬性時出錯:', error);
              }
              
              // 強制更新 DOM 元素
              const el = component.getEl();
              if (el) {
                el.textContent = contentToSync;
                console.log('✅ textarea DOM 元素內容已同步');
              }
            }
          }
        } catch (error) {
          console.warn('⚠️ 同步 editFormData 時出錯:', error);
        }
      }
      
      return newData;
    });
  };

  // 保存更改
  const handleSave = async () => {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ handleSave: 組件無效或已被銷毀');
      message.error('無法保存：組件無效或已被銷毀');
      return;
    }
    
    setIsLoading(true);
    try {
      // 根據組件類型更新屬性
      const tagName = component.get('tagName');
      if (!tagName) {
        throw new Error('組件標籤名稱無效');
      }
      
      switch (tagName.toLowerCase()) {
        case 'input':
          if (formData.type === 'radio' || formData.type === 'checkbox') {
            // 更新 radio/checkbox 組件
            component.setAttributes({
              type: formData.type,
              name: formData.name,
              value: formData.value,
              required: formData.required,
              disabled: formData.disabled
            });
            
            if (formData.type === 'checkbox') {
              component.setAttributes({ checked: formData.checked });
            }
          } else {
            // 更新普通 input 組件
            // 金額類型：HTML 用 type="text" + data-input-type="currency"；日期類型：type="text" + data-input-type="date"
            const isCurrency = formData.type === 'currency';
            const isDate = formData.type === 'date';
            const attributes = {
              type: (isCurrency || isDate) ? 'text' : formData.type,
              placeholder: formData.placeholder,
              value: formData.value,
              name: formData.name,
              required: formData.required,
              disabled: formData.disabled,
              readonly: formData.readonly,
              maxlength: formData.maxlength,
              minlength: formData.minlength,
              pattern: formData.pattern,
              title: formData.title
            };
            if (isCurrency) {
              attributes['data-input-type'] = 'currency';
              attributes['inputmode'] = 'decimal';
            }
            if (isDate) {
              attributes['data-input-type'] = 'date';
              if (formData.dateFormat) {
                attributes['data-date-format'] = formData.dateFormat;
              }
            }
            
            component.setAttributes(attributes);
          }
          break;
          
        case 'textarea':
          console.log('💾 保存 textarea 組件:', formData);
          
          // 獲取要保存的內容值（優先使用 defaultValue，其次使用 value）
          const contentToSave = formData.defaultValue ?? formData.value ?? '';
          console.log(' textarea 要保存的內容:', contentToSave);
          
          // 強制更新 DOM 元素（在設置組件內容之前）
          const el = component.getEl();
          if (el) {
            el.textContent = contentToSave;
            console.log('✅ textarea DOM 元素內容已更新:', contentToSave);
          }
          
          // 更新組件內容 - 使用多種方法確保內容被正確設置
          component.set('content', contentToSave);
          
          // 同時設置 innerHTML 和 textContent 屬性
          try {
            component.set('innerHTML', contentToSave);
            component.set('textContent', contentToSave);
            console.log('✅ textarea 組件 innerHTML 和 textContent 已設置');
          } catch (error) {
            console.warn('⚠️ 設置 innerHTML/textContent 時出錯:', error);
          }
          
          // 更新屬性
          component.setAttributes({
            placeholder: formData.placeholder,
            name: formData.name,
            required: formData.required,
            disabled: formData.disabled,
            readonly: formData.readonly,
            rows: formData.rows,
            cols: formData.cols,
            maxlength: formData.maxlength,
            minlength: formData.minlength
          });
          
          // 同時更新 editFormData 以保持一致性
          const textareaEditFormData = component.get('editFormData') || {};
          textareaEditFormData.defaultValue = contentToSave;
          textareaEditFormData.value = contentToSave;
          component.set('editFormData', textareaEditFormData);
          
          // 強制觸發組件重新渲染
          try {
            // 觸發組件內容變化事件
            component.trigger('change:content');
            console.log('✅ textarea 組件 change:content 事件已觸發');
            
            // 觸發組件更新事件
            component.trigger('component:update');
            console.log('✅ textarea 組件 component:update 事件已觸發');
            
            // 觸發組件重新渲染事件
            component.trigger('change:attributes');
            console.log('✅ textarea 組件 change:attributes 事件已觸發');
            
            // 觸發組件重新渲染事件
            component.trigger('change:innerHTML');
            console.log('✅ textarea 組件 change:innerHTML 事件已觸發');
          } catch (error) {
            console.warn('⚠️ 觸發組件事件時出錯:', error);
          }
          
          // 強制編輯器更新，確保 HTML 代碼同步
          if (grapesEditor && grapesEditor.refresh) {
            try {
              // 使用 setTimeout 避免在事件處理器中直接調用 refresh
              setTimeout(() => {
                if (grapesEditor && !grapesEditor.isDestroyed) {
                  // 強制重新設置組件內容
                  const selectedComponent = grapesEditor.getSelected();
                  if (selectedComponent && selectedComponent.get('tagName') === 'textarea') {
                    console.log(' 強制重新設置 textarea 組件內容...');
                    
                    // 使用多種方法設置內容
                    selectedComponent.set('content', contentToSave);
                    selectedComponent.set('innerHTML', contentToSave);
                    selectedComponent.set('textContent', contentToSave);
                    
                    // 強制觸發內容變化事件
                    selectedComponent.trigger('change:content');
                    selectedComponent.trigger('change:innerHTML');
                    
                    console.log('✅ textarea 組件內容已強制重新設置');
                  }
                  
                  // 刷新整個編輯器
                  grapesEditor.refresh();
                  console.log('✅ GrapesJS 編輯器已刷新，HTML 代碼已同步');
                  
                  // 驗證更新結果
                  const updatedHtml = grapesEditor.getHtml();
                  console.log('🔍 更新後的 HTML 內容:', updatedHtml);
                  
                  // 精確比對 textarea 的實際內容（不能用 includes，因為空字串或子字串都會誤判）
                  const textareaId = component.getId();
                  const textareaRegex = new RegExp(`id="${textareaId}"[^>]*>([\\s\\S]*?)<\\/textarea>`);
                  const textareaMatch = updatedHtml.match(textareaRegex);
                  const actualTextareaContent = textareaMatch ? textareaMatch[1] : null;
                  const htmlContentCorrect = (actualTextareaContent === contentToSave);
                  
                  if (htmlContentCorrect) {
                    console.log('✅ HTML 中 textarea 內容已正確:', contentToSave);
                  } else {
                    console.warn('⚠️ HTML 中 textarea 內容不正確，期望:', JSON.stringify(contentToSave), '實際:', JSON.stringify(actualTextareaContent));
                    
                    // 如果仍然沒有內容，嘗試更強力的方法
                    console.log('🔍 嘗試更強力的內容更新方法...');
                    
                    // 重新創建組件 - 使用正確的 GrapesJS API
                    const parent = selectedComponent.parent();
                    
                    if (parent && parent.components) {
                      // 獲取組件在父組件中的索引
                      let componentIndex = 0;
                      const components = parent.components();
                      for (let i = 0; i < components.length; i++) {
                        if (components[i] === selectedComponent) {
                          componentIndex = i;
                          break;
                        }
                      }
                      
                      // 創建新的 textarea 組件
                      const newTextareaHtml = `<textarea placeholder="${formData.placeholder || ''}" name="${formData.name || ''}" rows="${formData.rows || '4'}" cols="${formData.cols || '50'}" maxlength="${formData.maxlength || ''}" minlength="${formData.minlength || ''}" id="${selectedComponent.getId()}">${contentToSave}</textarea>`;
                      
                      // 替換舊組件
                      parent.components().remove(selectedComponent);
                      parent.components().add(newTextareaHtml, { at: componentIndex });
                      
                      console.log('✅ textarea 組件已重新創建，內容:', contentToSave);
                      
                      // 再次刷新編輯器
                      grapesEditor.refresh();
                      const finalHtml = grapesEditor.getHtml();
                      console.log('🔍 重新創建後的 HTML 內容:', finalHtml);
                    }
                  }
                }
              }, 100);
            } catch (error) {
              console.warn('⚠️ 刷新編輯器時出錯:', error);
            }
          }
          
          console.log('✅ textarea 組件保存完成');
          break;
          
        case 'select':
          component.setAttributes({
            name: formData.name,
            required: formData.required,
            disabled: formData.disabled,
            multiple: formData.multiple,
            size: formData.size
          });
          
          // 更新選項 - 修復 option 組件創建問題
          if (formData.options && formData.options.length > 0) {
            // 清空現有選項
            component.components().reset();
            
            // 使用正確的 GrapesJS 方法創建 option 組件
            formData.options.forEach(option => {
              // 創建 option 組件的 HTML 字符串
              const optionHtml = `<option value="${option.value || ''}" ${option.selected ? 'selected' : ''}>${option.text || ''}</option>`;
              
              // 使用 addComponent 方法添加組件
              component.components().add(optionHtml);
            });
          }
          break;
          
        case 'button':
          component.setAttributes({
            type: formData.type,
            name: formData.name,
            disabled: formData.disabled,
            form: formData.form,
            formaction: formData.formaction,
            formmethod: formData.formmethod,
            formnovalidate: formData.formnovalidate
          });
          component.set('content', formData.value);
          break;
          
        case 'label':
          component.setAttributes({
            for: formData.for,
            required: formData.required
          });
          component.set('content', formData.value);
          break;
          
        case 'td':
        case 'th':
          // 處理 table cell，保存文字內容
          // 優先從 editFormData.content 讀取，因為它是最可靠的數據源
          const editFormData = component.get('editFormData') || {};
          const cellContent = editFormData.content !== undefined 
            ? editFormData.content 
            : (formData.content || '');
          console.log('💾 保存 table cell 組件:', { tagName, content: cellContent, editFormDataContent: editFormData.content, formDataContent: formData.content });
          
          // 更新組件內容 - 使用多種方法確保內容被正確設置
          component.set('content', cellContent);
          component.set('innerHTML', cellContent);
          component.set('textContent', cellContent);
          
          // 同步更新 editFormData
          const cellEditFormData = component.get('editFormData') || {};
          cellEditFormData.content = cellContent;
          cellEditFormData.tagName = tagName;
          component.set('editFormData', cellEditFormData);
          
          // 強制觸發組件更新事件，確保 GrapesJS 同步
          // 注意：先更新組件狀態，再更新 DOM，最後才 refresh
          if (component.trigger) {
            component.trigger('change:content');
            component.trigger('change:innerHTML');
            component.trigger('change:textContent');
            component.trigger('component:update');
          }
          
          // 在事件觸發後，更新 DOM（確保組件狀態已更新）
          const cellEl = component.getEl();
          if (cellEl) {
            // 先清除所有子節點
            while (cellEl.firstChild) {
              cellEl.removeChild(cellEl.firstChild);
            }
            // 創建文本節點並添加
            const textNode = document.createTextNode(cellContent);
            cellEl.appendChild(textNode);
            // 同時設置 textContent 和 innerHTML 以確保兼容性
            cellEl.textContent = cellContent;
            cellEl.innerHTML = cellContent;
            console.log('✅ table cell DOM 已更新:', cellContent);
          }
          
          // 強制刷新編輯器，確保 HTML 代碼同步
          // 注意：refresh 可能會重新渲染 DOM，所以我們需要在 refresh 後再次確保內容正確
          if (grapesEditor && grapesEditor.refresh) {
            try {
              // 立即刷新，不等待
              grapesEditor.refresh();
              
              // refresh 後，再次確保 DOM 內容正確（因為 refresh 可能會重新渲染）
              setTimeout(() => {
                const cellElAfterRefresh = component.getEl();
                if (cellElAfterRefresh) {
                  const currentText = cellElAfterRefresh.textContent || cellElAfterRefresh.innerText || '';
                  if (currentText !== cellContent) {
                    console.log('🔧 refresh 後修復 table cell DOM:', { expected: cellContent, actual: currentText });
                    while (cellElAfterRefresh.firstChild) {
                      cellElAfterRefresh.removeChild(cellElAfterRefresh.firstChild);
                    }
                    const textNode = document.createTextNode(cellContent);
                    cellElAfterRefresh.appendChild(textNode);
                    cellElAfterRefresh.textContent = cellContent;
                    cellElAfterRefresh.innerHTML = cellContent;
                  }
                }
              }, 50);
              
              // 驗證內容是否正確保存（多次驗證，確保內容持久化）
              const verifyAndFix = (attempt = 1, maxAttempts = 3) => {
                setTimeout(() => {
                  if (grapesEditor && !grapesEditor.isDestroyed && component) {
                    const cellElAfter = component.getEl();
                    // 嘗試多種方法讀取 DOM 內容
                    const savedContent = cellElAfter ? (
                      cellElAfter.textContent || 
                      cellElAfter.innerText || 
                      cellElAfter.innerHTML?.replace(/<[^>]*>/g, '') || 
                      ''
                    ) : '';
                    const componentContent = component.get('content') || '';
                    const componentInnerHTML = component.get('innerHTML') || '';
                    const componentTextContent = component.get('textContent') || '';
                    
                    console.log(`🔍 驗證 table cell 內容 (嘗試 ${attempt}/${maxAttempts}):`, {
                      expected: cellContent,
                      domContent: savedContent,
                      componentContent: componentContent,
                      componentInnerHTML: componentInnerHTML,
                      componentTextContent: componentTextContent,
                      editFormDataContent: editFormData.content,
                      hasDOMElement: !!cellElAfter
                    });
                    
                    // 檢查是否需要修復（DOM 或組件內容不一致）
                    const needsFix = savedContent !== cellContent || 
                                    componentContent !== cellContent ||
                                    componentInnerHTML !== cellContent ||
                                    componentTextContent !== cellContent;
                    
                    if (needsFix) {
                      console.warn(`⚠️ table cell 內容未正確保存 (嘗試 ${attempt}/${maxAttempts}):`, { 
                        expected: cellContent, 
                        actualDOM: savedContent,
                        actualComponent: componentContent,
                        actualInnerHTML: componentInnerHTML,
                        actualTextContent: componentTextContent
                      });
                      
                      // 先更新組件狀態
                      component.set('content', cellContent);
                      component.set('innerHTML', cellContent);
                      component.set('textContent', cellContent);
                      
                      // 確保 editFormData 也更新
                      const currentEditFormData = component.get('editFormData') || {};
                      currentEditFormData.content = cellContent;
                      component.set('editFormData', currentEditFormData);
                      
                      // 觸發更新事件
                      if (component.trigger) {
                        component.trigger('change:content');
                        component.trigger('change:innerHTML');
                        component.trigger('change:textContent');
                        component.trigger('component:update');
                      }
                      
                      // 然後更新 DOM
                      if (cellElAfter) {
                        while (cellElAfter.firstChild) {
                          cellElAfter.removeChild(cellElAfter.firstChild);
                        }
                        const textNode = document.createTextNode(cellContent);
                        cellElAfter.appendChild(textNode);
                        cellElAfter.textContent = cellContent;
                        cellElAfter.innerHTML = cellContent;
                        console.log('🔧 已修復 table cell DOM 和組件狀態');
                      }
                      
                      // 如果還有嘗試次數，繼續驗證（不調用 refresh，避免清空內容）
                      if (attempt < maxAttempts) {
                        verifyAndFix(attempt + 1, maxAttempts);
                      } else {
                        console.error('❌ table cell 內容在多次嘗試後仍無法正確保存');
                        // 最後一次嘗試，調用 refresh 並立即修復
                        if (grapesEditor.refresh) {
                          grapesEditor.refresh();
                          setTimeout(() => {
                            const finalEl = component.getEl();
                            if (finalEl && (finalEl.textContent || finalEl.innerText) !== cellContent) {
                              while (finalEl.firstChild) {
                                finalEl.removeChild(finalEl.firstChild);
                              }
                              const textNode = document.createTextNode(cellContent);
                              finalEl.appendChild(textNode);
                              finalEl.textContent = cellContent;
                              finalEl.innerHTML = cellContent;
                              console.log('🔧 最後一次修復 table cell DOM');
                            }
                          }, 100);
                        }
                      }
                    } else {
                      console.log(`✅ table cell 內容已正確保存 (嘗試 ${attempt}/${maxAttempts}):`, cellContent);
                    }
                  }
                }, attempt === 1 ? 150 : 250); // 增加等待時間，確保 refresh 完成
              };
              
              verifyAndFix();
            } catch (error) {
              console.warn('⚠️ 刷新編輯器時出錯:', error);
            }
          }
          
          console.log('✅ table cell 組件保存完成');
          break;
          
        case 'img':
          console.log('💾 保存 img 組件:', {
            formDataSrc: formData.src,
            formDataAlt: formData.alt,
            formDataWidth: formData.width,
            formDataHeight: formData.height
          });
          
          // 優先從 formData 獲取 src，但如果是 placeholder 或 base64，則從組件模型獲取
          let imageSrc = formData.src || '';
          
          // 如果是 base64 格式，清除它（因為我們需要 URL）
          if (imageSrc && imageSrc.startsWith('data:image')) {
            console.warn('⚠️ 檢測到 base64 格式的圖片，需要清除並使用 URL');
            imageSrc = '';
          }
          
          // 如果 src 是 placeholder、no-image 或為空，嘗試從組件模型獲取
          // 優先順序：formData.src > editFormData.src > 組件屬性 src > 保持現狀（不重置為 no-image.jpg）
          if (!imageSrc || imageSrc.includes('via.placeholder.com') || imageSrc.includes('/assets/no-image.jpg')) {
            // 優先檢查 editFormData（這是最新的編輯值）
            const editFormData = component.get('editFormData');
            if (editFormData && editFormData.src && 
                !editFormData.src.startsWith('data:image') && 
                !editFormData.src.includes('via.placeholder.com') && 
                !editFormData.src.includes('/assets/no-image.jpg')) {
              imageSrc = editFormData.src;
              console.log('🔄 從 editFormData 使用有效的 src:', imageSrc);
            } else {
              // 如果 editFormData 沒有有效值，檢查組件屬性
              const existingSrc = component.getAttributes().src || '';
              if (existingSrc && 
                  !existingSrc.startsWith('data:image') && 
                  !existingSrc.includes('via.placeholder.com') && 
                  !existingSrc.includes('/assets/no-image.jpg')) {
                imageSrc = existingSrc;
                console.log('🔄 從組件屬性保留非 placeholder/no-image 的 src:', imageSrc);
              } else {
                // 如果所有來源都沒有有效值，且當前 src 是 no-image.jpg，保留它
                // 但不要將已上傳的圖片重置為 no-image.jpg
                if (!imageSrc || imageSrc.includes('/assets/no-image.jpg')) {
                  console.log('⚠️ 沒有找到有效的 src，保留當前值:', imageSrc || '(空)');
                  // 不強制設置為 no-image.jpg，避免覆蓋已上傳的圖片
                }
              }
            }
          }
          
          // 解析現有樣式，檢查是否有 width/height 在 CSS 中
          const existingStyle = component.getAttributes().style || '';
          const existingStyleObj = {};
          if (existingStyle) {
            existingStyle.split(';').forEach(rule => {
              const [key, value] = rule.split(':').map(s => s.trim());
              if (key && value) {
                existingStyleObj[key] = value;
              }
            });
          }
          
          // 構建樣式對象 - 優先使用 formData 中的值，如果沒有則保留現有的 CSS 值
          const styleObj = { ...existingStyleObj }; // 先保留所有現有樣式
          
          // 如果 formData 中有 width/height，優先使用它（用戶手動設置）
          if (formData.width) {
            styleObj.width = formData.width;
          }
          if (formData.height) {
            styleObj.height = formData.height;
          }
          
          // 如果 formData 中沒有 width/height，但 CSS 中有，則從 CSS 同步到屬性
          let widthToUse = formData.width || '';
          let heightToUse = formData.height || '';
          
          if (!widthToUse && styleObj.width) {
            widthToUse = styleObj.width;
            console.log('🔄 從 CSS 樣式同步 width 到屬性:', widthToUse);
          }
          if (!heightToUse && styleObj.height) {
            heightToUse = styleObj.height;
            console.log('🔄 從 CSS 樣式同步 height 到屬性:', heightToUse);
          }
          
          // 如果沒有明確設置，確保有默認的響應式樣式
          if (!formData.width && !styleObj.width && !widthToUse) {
            styleObj['max-width'] = '100%';
          }
          if (!formData.height && !styleObj.height && !heightToUse) {
            // 只有在沒有設置 height 時才設置 auto
            if (!styleObj.height) {
              styleObj['height'] = 'auto';
            }
          }
          
          // 將樣式對象轉換為字符串
          const styleStr = Object.entries(styleObj)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');
          
          // 更新組件屬性 - 確保 width/height 屬性和 CSS 樣式同步
          component.setAttributes({
            src: imageSrc,
            alt: formData.alt || '',
            width: widthToUse, // 使用同步後的寬度
            height: heightToUse, // 使用同步後的高度
            style: styleStr
          });
          
          // 強制更新 DOM 元素
          const imgEl = component.getEl();
          if (imgEl) {
            imgEl.src = imageSrc;
            if (formData.alt) {
              imgEl.alt = formData.alt;
            }
            
            // 同時設置 width/height 屬性和 CSS 樣式，確保兩者同步
            if (widthToUse) {
              imgEl.setAttribute('width', widthToUse);
              imgEl.width = widthToUse.replace(/px|%/, '') || ''; // 嘗試設置數值（如果可能）
            }
            if (heightToUse && heightToUse !== 'auto') {
              imgEl.setAttribute('height', heightToUse);
              imgEl.height = heightToUse.replace(/px|%/, '') || ''; // 嘗試設置數值（如果可能）
            }
            
            if (styleStr) {
              imgEl.style.cssText = styleStr;
            }
            console.log('✅ img DOM 元素已更新:', {
              src: imgEl.src,
              alt: imgEl.alt,
              width: imgEl.getAttribute('width'),
              height: imgEl.getAttribute('height'),
              styleWidth: imgEl.style.width,
              styleHeight: imgEl.style.height
            });
          }
          
          // 同步更新 editFormData - 使用同步後的寬高值
          const imgEditFormData = component.get('editFormData') || {};
          imgEditFormData.src = imageSrc;
          imgEditFormData.alt = formData.alt || '';
          imgEditFormData.width = widthToUse || '';
          imgEditFormData.height = heightToUse || '';
          imgEditFormData.style = styleStr;
          component.set('editFormData', imgEditFormData);
          
          // 確保 DOM 元素已更新
          if (imgEl) {
            // 強制設置 DOM 元素的 src，確保絕對 URL
            const absoluteSrc = imageSrc && imageSrc.startsWith('/')
              ? `${window.location.origin}${imageSrc}`
              : imageSrc;
            imgEl.src = absoluteSrc || '';
            imgEl.setAttribute('src', imageSrc || ''); // 同時設置屬性，確保 HTML 中也有
          }
          
          // 強制更新組件的 attributes，確保 getHtml() 返回正確的值
          // 使用 setAttributes 並觸發 change 事件 - 使用同步後的寬高值
          component.setAttributes({
            src: imageSrc,
            alt: formData.alt || '',
            width: widthToUse || '',
            height: heightToUse || '',
            style: styleStr
          }, { silent: false }); // 確保觸發 change 事件
          
          // 觸發組件更新事件，確保 HTML 同步
          component.trigger('change:attributes');
          component.trigger('component:update');
          
          // 強制刷新編輯器，確保 HTML 同步
          setTimeout(() => {
            grapesEditor.refresh();
            // 驗證組件屬性是否正確
            const verifySrc = component.getAttributes().src;
            console.log('🔍 驗證組件屬性 src:', verifySrc);
            if (verifySrc !== imageSrc) {
              console.warn('⚠️ 組件屬性 src 不匹配，強制更新');
              component.setAttributes({ src: imageSrc }, { silent: false });
            }
          }, 100);
          
          console.log('✅ img 組件保存完成:', {
            finalSrc: imageSrc,
            finalAlt: formData.alt,
            componentSrc: component.getAttributes().src,
            domSrc: imgEl ? imgEl.getAttribute('src') : 'N/A'
          });
          break;

        case 'div':
          if (component.getAttributes()?.['data-gjs-type'] === 'text') {
            // 文字元素：套用內容與樣式
            const contentToSave = (typeof formData.content === 'string' ? formData.content : '') || '';
            const styleParts = [];
            if (formData.textAlign) styleParts.push(`text-align: ${formData.textAlign}`);
            if (formData.fontFamily) styleParts.push(`font-family: ${formData.fontFamily}`);
            if (formData.fontSize) styleParts.push(`font-size: ${formData.fontSize}`);
            const ft = formData.fontStyleType || 'normal';
            if (ft === 'bold') styleParts.push('font-weight: bold');
            else if (ft === 'italic') styleParts.push('font-style: italic');
            else if (ft === 'bold-italic') { styleParts.push('font-weight: bold'); styleParts.push('font-style: italic'); }
            else if (ft === 'underline') styleParts.push('text-decoration: underline');
            else { styleParts.push('font-weight: normal'); styleParts.push('font-style: normal'); styleParts.push('text-decoration: none'); }
            const textBlockStyle = styleParts.join('; ');
            component.setAttributes({ style: textBlockStyle }, { silent: false });
            component.components(contentToSave);
            component.set('content', contentToSave, { silent: false });
            const textBlockEl = component.getEl();
            if (textBlockEl) {
              textBlockEl.style.cssText = textBlockStyle;
              textBlockEl.textContent = contentToSave;
            }
            const textEditFormData = component.get('editFormData') || {};
            Object.assign(textEditFormData, formData);
            component.set('editFormData', textEditFormData);
            if (grapesEditor && grapesEditor.trigger) grapesEditor.trigger('component:update', component);
          } else {
            Object.keys(formData).forEach(key => {
              if (key !== 'tagName' && key !== 'content') component.setAttributes({ [key]: formData[key] });
            });
            if (formData.content !== undefined) component.set('content', formData.content);
          }
          break;

        case 'p':
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6': {
          const contentToSave = (typeof formData.content === 'string' ? formData.content : '') || '';
          const styleParts = [];
          if (formData.textAlign) styleParts.push(`text-align: ${formData.textAlign}`);
          if (formData.fontFamily) styleParts.push(`font-family: ${formData.fontFamily}`);
          if (formData.fontSize) styleParts.push(`font-size: ${formData.fontSize}`);
          const ft = formData.fontStyleType || 'normal';
          if (ft === 'bold') styleParts.push('font-weight: bold');
          else if (ft === 'italic') styleParts.push('font-style: italic');
          else if (ft === 'bold-italic') { styleParts.push('font-weight: bold'); styleParts.push('font-style: italic'); }
          else if (ft === 'underline') styleParts.push('text-decoration: underline');
          else { styleParts.push('font-weight: normal'); styleParts.push('font-style: normal'); styleParts.push('text-decoration: none'); }
          const textBlockStyle = styleParts.join('; ');
          component.setAttributes({ style: textBlockStyle }, { silent: false });
          component.components(contentToSave);
          component.set('content', contentToSave, { silent: false });
          const textBlockEl = component.getEl();
          if (textBlockEl) {
            textBlockEl.style.cssText = textBlockStyle;
            textBlockEl.textContent = contentToSave;
          }
          const textEditFormData = component.get('editFormData') || {};
          Object.assign(textEditFormData, formData);
          component.set('editFormData', textEditFormData);
          if (grapesEditor && grapesEditor.trigger) grapesEditor.trigger('component:update', component);
          break;
        }
          
        default:
          // 更新通用屬性
          Object.keys(formData).forEach(key => {
            if (key !== 'tagName' && key !== 'content') {
              component.setAttributes({ [key]: formData[key] });
            }
          });
          
          if (formData.content !== undefined) {
            component.set('content', formData.content);
          }
          break;
      }
      

      
      // 強制重新渲染編輯器內容
      if (component.get('tagName').toLowerCase() === 'textarea') {
        console.log('🔄 強制更新 textarea 內容:', {
          defaultValue: formData.defaultValue,
          value: formData.value,
          finalContent: formData.defaultValue || formData.value || ''
        });
        
        // 確保 DOM 元素也被更新
        const el = component.getEl();
        if (el) {
          el.textContent = formData.defaultValue || formData.value || '';
          console.log('✅ DOM 元素內容已更新');
        }
        
        // 同時更新 editFormData 以保持一致性（在 switch 內已經處理，這裡僅為確保同步）
        const textareaFormData = component.get('editFormData') || {};
        textareaFormData.defaultValue = formData.defaultValue || formData.value || '';
        textareaFormData.value = formData.defaultValue || formData.value || '';
        component.set('editFormData', textareaFormData);
        console.log('✅ textarea editFormData 已同步:', textareaFormData);
        

        
        // 強制重新渲染編輯器
        if (component.editor) {
          component.editor.refresh();
        }
        
        // 延遲觸發編輯器刷新，確保所有變更都已應用
        setTimeout(() => {
          if (grapesEditor && grapesEditor.refresh) {
            grapesEditor.refresh();
          }
        }, 200);
      }
      
      message.success('組件已成功更新！');
      onClose();
    } catch (error) {
      console.error('保存組件失敗:', error);
      message.error('保存失敗，請重試');
    } finally {
      setIsLoading(false);
    }
  };

  // 根據組件類型渲染對應的編輯器
  const renderEditor = () => {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ renderEditor: 組件無效或已被銷毀');
      return <div>無法編輯：組件無效或已被銷毀</div>;
    }
    
    try {
      const tagName = component.get('tagName');
      if (!tagName) {
        console.warn('⚠️ renderEditor: 組件標籤名稱無效');
        return <div>無法編輯：組件標籤名稱無效</div>;
      }
      
      switch (tagName.toLowerCase()) {
        case 'input':
          const inputType = component.getAttributes().type || 'text';
          if (inputType === 'radio') {
            return <RadioEditor formData={formData} onFormChange={handleFormChange} />;
          } else if (inputType === 'checkbox') {
            return <CheckboxEditor formData={formData} onFormChange={handleFormChange} />;
          } else {
            return <TextInputEditor formData={formData} onFormChange={handleFormChange} />;
          }
        
        case 'textarea':
          return <TextAreaEditor formData={formData} onFormChange={handleFormChange} />;
          
        case 'select':
          return <SelectEditor formData={formData} onFormChange={handleFormChange} />;
          
        case 'button':
          return <ButtonEditor formData={formData} onFormChange={handleFormChange} />;
          
        case 'label':
          return <LabelEditor formData={formData} onFormChange={handleFormChange} />;

        case 'div':
          if (component.getAttributes()?.['data-gjs-type'] === 'text') {
            return <TextBlockEditor formData={formData} onFormChange={handleFormChange} />;
          }
          return <TextInputEditor formData={formData} onFormChange={handleFormChange} />;

        case 'p':
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return <TextBlockEditor formData={formData} onFormChange={handleFormChange} />;
          
        case 'img':
          return <ImageEditor formData={formData} onFormChange={handleFormChange} />;
          
        case 'td':
        case 'th':
          // 使用專門的 TableCellEditor 來編輯 table cell 的文字內容
          // 這個編輯器直接處理 content 欄位，不需要映射
          return <TableCellEditor 
            formData={formData}
            onFormChange={handleFormChange}
          />;
          
        default:
          return <TextInputEditor formData={formData} onFormChange={handleFormChange} />;
      }
    } catch (error) {
      console.error('❌ renderEditor 發生錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
      return <div>編輯器載入失敗，請重試</div>;
    }
  };

  return (
    <div>
      {/* 編輯器內容 */}
      {renderEditor()}
      
      {/* 操作按鈕 */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        justifyContent: 'flex-end', 
        marginTop: '20px', 
        paddingTop: '16px', 
        borderTop: '1px solid #e8e8e8' 
      }}>
        <Button onClick={onClose}>
          {t('eformDesigner.cancel')}
        </Button>
        <Button 
          type="primary" 
          onClick={handleSave}
          loading={isLoading}
        >
          {t('eformDesigner.save')}
        </Button>
      </div>
    </div>
  );
};

export default EFormDesigner; 